import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, Link } from 'react-router-dom';
import { auth, db, functions, storage } from './firebase';
import { GoogleAuthProvider, signInWithPopup } from "firebase/auth";
import { doc, getDoc, collection, addDoc, serverTimestamp } from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { ref, getDownloadURL, uploadBytes, deleteObject } from "firebase/storage";
import ActivityMap from './ActivityMap'; // Import the new ActivityMap component
import ActivityMap3D, { DEFAULT_MAP_CAPTURE_HEIGHT, DEFAULT_MAP_CAPTURE_WIDTH } from './ActivityMap3D'; // Import the 3D map component
// import ActivityPhotos from './ActivityPhotos'; // Import the ActivityPhotos component - conditionally imported below
import { useWaterBasedStyles } from './hooks/useWaterBasedStyles'; // Import water detection hook
import { useAdminView } from './contexts/AdminViewContext'; // Import admin view context
import CreditsCounter from './components/CreditsCounter';
import PromoCard from './components/PromoCard';
import './App.css'; // Assuming common styles are here
import './HistoryPage.css'; // Import HistoryPage styles to reuse them
import './SharePage.css'; // Import SharePage styles for the Free Plan card
import './LandingPage.css'; // Import LandingPage-specific styles
import { usePostHog } from 'posthog-js/react';

// Import your logos with correct casing
import urbanViceLogo from './assets/logos/Urban_Vice_Logo.png';
import mythicLogo from './assets/logos/Mythic_Logo.png';
import stravaConnectButton from './assets/strava/btn_strava_connect_with_orange.svg';
import japaneseLogo from './assets/logos/Japanese_Logo.png';
import classicRtsLogo from './assets/logos/Classic_RTS_Logo.png';
import logo80sTdF from './assets/logos/80ies_TdF_Logo.png';
import logoAboriginal from './assets/logos/Aboriginal_Songline_Logo.png';
import logoAztec from './assets/logos/Aztec_Codex_Logo.png';
import logoCaravanAtlas from './assets/logos/Caravan_Atlas_Logo.png';
import modernVueltaLogo from './assets/logos/Modern_Vuelta_Logo.png';

// Strava OAuth Configuration
const STRAVA_CLIENT_ID = "159733";
const STRAVA_SIGNIN_REDIRECT_URI = window.location.origin + "/strava-auth-callback";
const STRAVA_SCOPE = "activity:read,activity:read_all,activity:write";

const MAP_CAPTURE_ASPECT_RATIO = DEFAULT_MAP_CAPTURE_HEIGHT / DEFAULT_MAP_CAPTURE_WIDTH;
const COLLAPSED_MAP_WIDTH = 200;
const COLLAPSED_MAP_HEIGHT = Math.round(COLLAPSED_MAP_WIDTH * MAP_CAPTURE_ASPECT_RATIO);
const LIST_MAP_WIDTH = 320;
const LIST_MAP_HEIGHT = Math.round(LIST_MAP_WIDTH * MAP_CAPTURE_ASPECT_RATIO);
const MOBILE_MAP_CAPTURE_WIDTH = 512;
const MOBILE_MAP_CAPTURE_HEIGHT = Math.round(MOBILE_MAP_CAPTURE_WIDTH * MAP_CAPTURE_ASPECT_RATIO);
const DESKTOP_MAP_CAPTURE_WIDTH = 640;
const DESKTOP_MAP_CAPTURE_HEIGHT = Math.round(DESKTOP_MAP_CAPTURE_WIDTH * MAP_CAPTURE_ASPECT_RATIO);

// Lazy load ActivityPhotos component for admin use only
const ActivityPhotosAdmin = React.lazy(() => import('./ActivityPhotos'));

function LandingPage({ user, setShowNavigation }) {
    const { t } = useTranslation();
    const [stravaConnected, setStravaConnected] = useState(false);
    const [activities, setActivities] = useState([]);
    const [isLoadingActivities, setIsLoadingActivities] = useState(false);
    const [promptTemplates, setPromptTemplates] = useState([]);
    const [isLoadingTemplates, setIsLoadingTemplates] = useState(false);
    const [heroImages, setHeroImages] = useState([]);
    const [currentHeroIndex, setCurrentHeroIndex] = useState(0);
    const [heroImageConfigs] = useState([
        { id: '6eJRSSXzTErOT0IBY9yG', logo: mythicLogo }, // Mythic
        { id: 'zDgxyKwTgEWNtAuymBH7', logo: japaneseLogo }, // Japanese
        { id: '7P9yopXDhZfVoqmlohTB', logo: classicRtsLogo }, // Classic RTS
        { id: 'kpuPQFzpaWe6x2d0bN79', logo: urbanViceLogo }, // Urban Vice
        { id: 'jaj9jHyqDOpUR1rNbg3x', logo: logo80sTdF }, // 80s TdF
        { id: '4kq3Q9nRSPDW8kpMsRcu', logo: logoAboriginal }, // Aboriginal
        { id: 'eRNCWm0LnAc2CEmk5ECV', logo: logoAztec }, // Aztec
        { id: 'GRfW22rqWYspKZxRBekn', logo: logoCaravanAtlas }, // Caravan Atlas
        { id: 'HegoMDylo4IfdIQGzihF', logo: modernVueltaLogo }, // Modern Vuelta (logo only)
    ]);
    const intervalRef = useRef(null);
    
    // Add state for smooth image transitions
    const [nextHeroImageData, setNextHeroImageData] = useState(null);
    const [isTransitioning, setIsTransitioning] = useState(false);

    // Add these missing state variables if they don't exist
    const [isConnecting, setIsConnecting] = useState(false);
    const [currentHeroImage, setCurrentHeroImage] = useState(null);
    const [isDragging, setIsDragging] = useState(false);
    
    // Add this ref if it doesn't exist
    const heroContainerRef = useRef(null);

    // Pagination state for activities
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 9;

    // Sorting state for activities - Set Date as default
    const [sortBy, setSortBy] = useState('start_date');
    const [sortOrder, setSortOrder] = useState('desc');

    // Pagination state for map styles
    const [currentStylePage, setCurrentStylePage] = useState(1);
    const [styleSortBy, setStyleSortBy] = useState('createdAt');
    const [styleSortOrder, setStyleSortOrder] = useState('desc');

    // Add new state for step 3
    const [selectedActivity, setSelectedActivity] = useState(null);
    const [selectedTemplate, setSelectedTemplate] = useState(null);
    const [selectedTemplateGroup, setSelectedTemplateGroup] = useState(null);
    const [isGenerating, setIsGenerating] = useState(false);
    const [statusError, setStatusError] = useState('');

    // Add new state for section visibility
    const [isActivitiesSectionExpanded, setIsActivitiesSectionExpanded] = useState(true);
    const [isStylesSectionExpanded, setIsStylesSectionExpanded] = useState(false);

    // Add state for animation
    const [isAnimating, setIsAnimating] = useState(false);
    const [sliderPosition, setSliderPosition] = useState(80);
    const isDraggingRef = useRef(false);
    const navigate = useNavigate();

    // Add monthly usage state
    const [monthlyUsage, setMonthlyUsage] = useState({ currentCount: 0, monthlyLimit: 10, remaining: 10 });
    const [isLoadingUsage, setIsLoadingUsage] = useState(true);

    // Add new state for hero image data
    const [heroImageData, setHeroImageData] = useState(null);
    const [isLoadingHeroImage, setIsLoadingHeroImage] = useState(true);

    // Add state to track if we're on mobile
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
    const [viewportWidth, setViewportWidth] = useState(window.innerWidth);
    const renderCaptureWidth = isMobile ? MOBILE_MAP_CAPTURE_WIDTH : DESKTOP_MAP_CAPTURE_WIDTH;
    const renderCaptureHeight = isMobile ? MOBILE_MAP_CAPTURE_HEIGHT : DESKTOP_MAP_CAPTURE_HEIGHT;
    const collapsedPreviewWidth = isMobile ? Math.max(200, Math.min(viewportWidth - 48, 360)) : COLLAPSED_MAP_WIDTH;
    const collapsedPreviewHeight = Math.round(collapsedPreviewWidth * MAP_CAPTURE_ASPECT_RATIO);
    const listPreviewWidth = isMobile ? Math.max(250, viewportWidth - 48) : LIST_MAP_WIDTH;
    const listPreviewHeight = Math.round(listPreviewWidth * MAP_CAPTURE_ASPECT_RATIO);

    // Add new state for water analysis
    const [waterAnalysis, setWaterAnalysis] = useState(null);
    const [showWaterAnalysis, setShowWaterAnalysis] = useState(false);
    
    // Add admin state and water analysis for all activities
    // Get admin view context
    const { isAdmin } = useAdminView();
    const [activityWaterAnalysis, setActivityWaterAnalysis] = useState({}); // Store analysis for all activities
    const [waterThreshold, setWaterThreshold] = useState(5.0);
    
    // Add state for toggling water percentage display (keyboard shortcut)
    const [showWaterPercentages, setShowWaterPercentages] = useState(false);
    
    // Add state for toggling activity photos (admin only)
    const [showActivityPhotos, setShowActivityPhotos] = useState(false);
    


    // Use refs to hold current values for stable callbacks
    const isStylesSectionExpandedRef = useRef(isStylesSectionExpanded);
    isStylesSectionExpandedRef.current = isStylesSectionExpanded;
    const selectedActivityRef = useRef(selectedActivity);
    selectedActivityRef.current = selectedActivity;

    // Filter prompt templates based on admin view mode
    const filteredPromptTemplates = useMemo(() => {
        return promptTemplates.filter(template => {
            // If in admin view mode, show all templates
            if (isAdmin) return true;
            // If not in admin view mode, hide admin-only templates
            return !template.adminOnly;
        });
    }, [promptTemplates, isAdmin]);
    const templateGroups = useMemo(() => {
        if (!filteredPromptTemplates || filteredPromptTemplates.length === 0) {
            return [];
        }
        const groupsMap = new Map();
        filteredPromptTemplates.forEach((template) => {
            const baseId = template.variantOf || template.id;
            if (!groupsMap.has(baseId)) {
                groupsMap.set(baseId, { id: baseId, variants: [] });
            }
            const group = groupsMap.get(baseId);
            if (!group.variants.some((existing) => existing.id === template.id)) {
                group.variants.push(template);
            }
        });

        return Array.from(groupsMap.values()).map((group) => {
            const sortedVariants = group.variants.slice().sort((a, b) => {
                const aIs3D = (a.mapInputType || '2d') === '3d' ? 1 : 0;
                const bIs3D = (b.mapInputType || '2d') === '3d' ? 1 : 0;
                if (aIs3D !== bIs3D) {
                    return aIs3D - bIs3D;
                }
                return (a.name || '').localeCompare(b.name || '');
            });

            const baseTemplateCandidate = sortedVariants.find((variant) => (variant.mapInputType || '2d') === '2d' && !variant.variantOf)
                || sortedVariants.find((variant) => (variant.mapInputType || '2d') === '2d')
                || sortedVariants[0];

            const baseTemplate = baseTemplateCandidate || sortedVariants[0];

            return {
                id: baseTemplate?.id || group.id,
                groupId: group.id,
                baseTemplate,
                variants: sortedVariants,
                has3DVariant: sortedVariants.some((variant) => (variant.mapInputType || '2d') === '3d'),
            };
        });
    }, [filteredPromptTemplates]);

    const aggregatedTemplates = useMemo(() => {
        return templateGroups.map((group) => {
            const baseTemplate = group.baseTemplate || group.variants[0];
            const fallbackName = baseTemplate?.name || group.groupId;
            return {
                ...baseTemplate,
                id: baseTemplate?.id || group.groupId,
                name: fallbackName,
                variants: group.variants,
                has3DVariant: group.has3DVariant,
                groupId: group.groupId,
                mapInputType: baseTemplate?.mapInputType || '2d',
                baseTemplate,
                baseTemplateId: baseTemplate?.id || group.groupId,
            };
        });
    }, [templateGroups]);

    const selectedTemplateLabel = useMemo(() => {
        if (!selectedTemplate) {
            return null;
        }
        const variantType = (selectedTemplate.mapInputType || '2d') === '3d' ? '3D' : '2D';
        if (selectedTemplateGroup && selectedTemplateGroup.variants && selectedTemplateGroup.variants.length > 1) {
            const baseName = selectedTemplateGroup.baseTemplate?.name || selectedTemplate.name;
            return `${baseName} (${variantType})`;
        }
        return selectedTemplate.name;
    }, [selectedTemplate, selectedTemplateGroup]);

    // This effect syncs the selected template group when templates change
    // Only run when the template group ID changes, not on every aggregatedTemplates reference change
    useEffect(() => {
        if (!selectedTemplateGroup) {
            return;
        }
        const updatedGroup = aggregatedTemplates.find((template) =>
            template.groupId === selectedTemplateGroup.groupId || template.id === selectedTemplateGroup.id
        );
        if (!updatedGroup) {
            setSelectedTemplateGroup(null);
            setSelectedTemplate(null);
            return;
        }

        // Only update if the variants actually changed (compare by length and IDs)
        const variantsChanged =
            updatedGroup.variants.length !== selectedTemplateGroup.variants?.length ||
            !updatedGroup.variants.every((v, i) => v.id === selectedTemplateGroup.variants?.[i]?.id);

        if (variantsChanged) {
            setSelectedTemplateGroup(updatedGroup);
            if (selectedTemplate) {
                const matchingVariant = updatedGroup.variants.find((variant) => variant.id === selectedTemplate.id);
                if (matchingVariant) {
                    setSelectedTemplate(matchingVariant);
                } else {
                    const fallbackVariant = updatedGroup.variants.find((variant) => (variant.mapInputType || '2d') === '2d')
                        || updatedGroup.variants[0];
                    setSelectedTemplate(fallbackVariant || null);
                }
            }
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedTemplateGroup?.groupId, selectedTemplate?.id]);

    // Add state for 3D map toggle
    const [use3DMap, setUse3DMap] = useState(false);
    const [is3DSelectionPending, setIs3DSelectionPending] = useState(false);
    const [pending3DVariantId, setPending3DVariantId] = useState(null);
    const [threeDRenderProgress, setThreeDRenderProgress] = useState(0);
    const threeDProgressTimerRef = useRef(null);

    const clear3DProgressTimer = useCallback(() => {
        if (threeDProgressTimerRef.current) {
            clearInterval(threeDProgressTimerRef.current);
            threeDProgressTimerRef.current = null;
        }
    }, []);

    const handle3DRenderStart = useCallback(() => {
        if (!pending3DVariantId) {
            return;
        }

        setIs3DSelectionPending(true);
        setThreeDRenderProgress((prev) => (prev > 5 ? prev : 5));

        if (threeDProgressTimerRef.current) {
            return;
        }

        threeDProgressTimerRef.current = setInterval(() => {
            setThreeDRenderProgress((prev) => {
                if (prev >= 90) {
                    clear3DProgressTimer();
                    return prev;
                }
                return Math.min(prev + 5, 90);
            });
        }, 300);
    }, [pending3DVariantId, clear3DProgressTimer]);

    const handle3DRenderComplete = useCallback(() => {
        clear3DProgressTimer();
        setThreeDRenderProgress(100);
        setIs3DSelectionPending(false);
        setPending3DVariantId(null);
    }, [clear3DProgressTimer]);

    useEffect(() => {
        return () => {
            clear3DProgressTimer();
        };
    }, [clear3DProgressTimer]);

    useEffect(() => {
        if (selectedTemplate && (selectedTemplate.mapInputType || '2d') === '3d') {
            clear3DProgressTimer();
            setIs3DSelectionPending(true);
            setPending3DVariantId(selectedTemplate.id || null);
            setThreeDRenderProgress(5);
        } else {
            clear3DProgressTimer();
            setIs3DSelectionPending(false);
            setPending3DVariantId(null);
            setThreeDRenderProgress(0);
        }
    }, [selectedActivity?.id, selectedTemplate, clear3DProgressTimer]);

    const is3DVariantSelected = selectedTemplate ? (selectedTemplate.mapInputType || '2d') === '3d' : false;
    const shouldRender3DMap = Boolean(
        is3DVariantSelected ||
        is3DSelectionPending ||
        use3DMap
    );
    const shouldShow3DPreview = shouldRender3DMap && (use3DMap || is3DVariantSelected);

    const heroAnimationEnabled = !user;

    // Use the water-based styles hook with filtered templates
    const {
        availableStyles,
        recommendedStyles,
        defaultStyles,
        waterSummary,
        isWaterAnalyzed
    } = useWaterBasedStyles(aggregatedTemplates, waterAnalysis, waterThreshold);
    const [captured3DImage, setCaptured3DImage] = useState(null);
    const captureMapCallbacksRef = useRef({});
    const active3DMapRef = useRef({ activityId: null, captureFunc: null });

    // Add useEffect to handle screen size changes
    useEffect(() => {
        const handleResize = () => {
            setViewportWidth(window.innerWidth);
            setIsMobile(window.innerWidth <= 768);
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    useEffect(() => {
        if (selectedTemplate && (selectedTemplate.mapInputType || '2d') === '3d') {
            setUse3DMap(true);
        }
    }, [selectedTemplate]);

    // Add keyboard shortcuts for toggling features
    useEffect(() => {
        const handleKeydown = (event) => {
            // Ctrl+W to toggle water percentage display
            if (event.ctrlKey && event.key === 'w') {
                event.preventDefault(); // Prevent browser default (close tab)
                setShowWaterPercentages(prev => {
                    const newValue = !prev;
                    console.log(`Water percentage display ${newValue ? 'enabled' : 'disabled'} via keyboard shortcut`);
                    
                    // Show a brief notification
                    const notification = document.createElement('div');
                    notification.textContent = `Water percentages ${newValue ? 'shown' : 'hidden'} (Ctrl+W)`;
                    notification.style.cssText = `
                        position: fixed;
                        top: 20px;
                        right: 20px;
                        background: #1f2937;
                        color: white;
                        padding: 12px 16px;
                        border-radius: 6px;
                        font-size: 14px;
                        font-weight: 500;
                        z-index: 10000;
                        box-shadow: 0 4px 12px rgba(0,0,0,0.3);
                        transition: opacity 0.3s ease;
                    `;
                    document.body.appendChild(notification);
                    
                    // Remove notification after 2 seconds
                    setTimeout(() => {
                        notification.style.opacity = '0';
                        setTimeout(() => {
                            if (notification.parentNode) {
                                notification.parentNode.removeChild(notification);
                            }
                        }, 300);
                    }, 2000);
                    
                    return newValue;
                });
            }
            
            // Ctrl+P to toggle activity photos (admin only)
            if (event.ctrlKey && event.key === 'p' && isAdmin) {
                event.preventDefault();
                setShowActivityPhotos(prev => {
                    const newValue = !prev;
                    console.log(`Activity photos display ${newValue ? 'enabled' : 'disabled'} via keyboard shortcut (admin)`);
                    
                    // Show a brief notification
                    const notification = document.createElement('div');
                    notification.textContent = `Activity photos ${newValue ? 'shown' : 'hidden'} (Ctrl+P)`;
                    notification.style.cssText = `
                        position: fixed;
                        top: 20px;
                        right: 20px;
                        background: #1f2937;
                        color: white;
                        padding: 12px 16px;
                        border-radius: 6px;
                        font-size: 14px;
                        font-weight: 500;
                        z-index: 10000;
                        box-shadow: 0 4px 12px rgba(0,0,0,0.3);
                        transition: opacity 0.3s ease;
                    `;
                    document.body.appendChild(notification);
                    
                    // Remove notification after 2 seconds
                    setTimeout(() => {
                        notification.style.opacity = '0';
                        setTimeout(() => {
                            if (notification.parentNode) {
                                notification.parentNode.removeChild(notification);
                            }
                        }, 300);
                    }, 2000);
                    
                    return newValue;
                });
            }
        };

        document.addEventListener('keydown', handleKeydown);
        return () => document.removeEventListener('keydown', handleKeydown);
    }, [isAdmin]);

    // Remove admin detection effect - now handled by AdminViewContext

    // Load water threshold from settings - only for logged-in users
    useEffect(() => {
        const loadWaterThreshold = async () => {
            // Only load water threshold if user is logged in
            if (!user) {
                console.log('Skipping water threshold loading - no user logged in');
                setWaterThreshold(5.0); // Use default value
                return;
            }
            
            try {
                const settingsDocRef = doc(db, 'settings', 'mapGeneration');
                const docSnap = await getDoc(settingsDocRef);
                if (docSnap.exists()) {
                    const settingsData = docSnap.data();
                    setWaterThreshold(settingsData?.waterThreshold || 5.0);
                } else {
                    setWaterThreshold(5.0); // Default value
                }
            } catch (error) {
                console.error('Error loading water threshold:', error);
                setWaterThreshold(5.0); // Default on error
            }
        };

        loadWaterThreshold();
    }, [user]);

    // Stable callback for the selected activity's detailed analysis (affects style filtering)
    const handleWaterAnalysis = useCallback((analysis) => {
        if (localStorage.getItem('waterDetectionDebug') === 'true') {
            console.log('LandingPage: Main water analysis handler triggered for selected activity:', analysis.activity.id);
        }
        setWaterAnalysis(analysis);
        setShowWaterAnalysis(true);
        if (analysis && !isStylesSectionExpandedRef.current && selectedActivityRef.current) {
            setIsStylesSectionExpanded(true);
        }
    }, []);

    // Stable callback to store analysis for any activity (used for all users, displayed for admins)
    const handleActivityWaterAnalysis = useCallback((activityId, analysis) => {
        if (localStorage.getItem('waterDetectionDebug') === 'true') {
            console.log(`LandingPage: Storing water analysis for activity ${activityId} (admin: ${isAdmin}):`, {
                waterPercentage: analysis.waterPercentage,
                totalPixels: analysis.totalPixels,
                userType: isAdmin ? 'admin' : 'non-admin'
            });
        }
        setActivityWaterAnalysis(prev => ({
            ...prev,
            [activityId]: analysis
        }));
    }, [isAdmin]);

    // A single, stable callback to pass to all ActivityMap components
    // This ensures water analysis is performed for ALL users equally - only display differs
    const handleWaterAnalysisCallback = useCallback((analysis) => {
        if (!analysis || !analysis.activity) return;

        const activityId = analysis.activity.id;

        // Check if we already have analysis for this activity
        const existingAnalysis = activityWaterAnalysis[activityId];

        if (existingAnalysis) {
            // If we already have analysis, only update if the new analysis has more pixels (higher resolution)
            // This prevents overwriting high-res analysis with low-res analysis from collapsed previews
            if (analysis.totalPixels <= existingAnalysis.totalPixels) {
                if (localStorage.getItem('waterDetectionDebug') === 'true') {
                    console.log(`Skipping re-analysis for activity ${activityId}: existing analysis has higher/equal resolution (${existingAnalysis.totalPixels} >= ${analysis.totalPixels} pixels)`);
                }

                // If this is the currently selected activity, use the existing analysis for style filtering
                if (selectedActivityRef.current?.id === activityId) {
                    handleWaterAnalysis(existingAnalysis);
                }
                return;
            } else {
                if (localStorage.getItem('waterDetectionDebug') === 'true') {
                    console.log(`Updating analysis for activity ${activityId}: new analysis has higher resolution (${analysis.totalPixels} vs ${existingAnalysis.totalPixels} pixels)`);
                }
            }
        }

        // Store water analysis for ALL users (ensures consistent behavior)
        // Admins can see this data in UI, non-admins get same analysis but no display
        handleActivityWaterAnalysis(activityId, analysis);

        // If this is the currently selected activity, update the main style-filtering state
        // This affects style filtering and works identically for all users
        if (selectedActivityRef.current?.id === activityId) {
            handleWaterAnalysis(analysis);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [handleActivityWaterAnalysis, handleWaterAnalysis]);

    // Computed values for pagination
    const sortedActivities = [...activities].sort((a, b) => {
        if (sortBy === 'name') {
            const nameA = a.name.toLowerCase();
            const nameB = b.name.toLowerCase();
            return sortOrder === 'asc'
                ? nameA.localeCompare(nameB)
                : nameB.localeCompare(nameA);
        } else if (sortBy === 'water_percentage' && showWaterPercentages) {
            // Water percentage sorting (available when display is enabled via Ctrl+W)
            const waterA = activityWaterAnalysis[a.id]?.waterPercentage || 0;
            const waterB = activityWaterAnalysis[b.id]?.waterPercentage || 0;
            return sortOrder === 'asc' ? waterA - waterB : waterB - waterA;
        } else { // sort by date
            const dateA = new Date(a.start_date);
            const dateB = new Date(b.start_date);
            return sortOrder === 'asc'
                ? dateA - dateB
                : dateB - dateA;
        }
    });

    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const currentActivities = sortedActivities.slice(startIndex, endIndex);
    const totalPages = Math.ceil(sortedActivities.length / itemsPerPage);

    // Custom style order preferences
    const styleOrderPriority = ['Mythic', 'Japanese', 'Classic', '80', 'Urban'];

    // Helper function to get priority index for a style
    const getStylePriority = (styleName) => {
        for (let i = 0; i < styleOrderPriority.length; i++) {
            if (styleName.toLowerCase().startsWith(styleOrderPriority[i].toLowerCase())) {
                return i;
            }
        }
        return styleOrderPriority.length; // Return length for styles not in priority list
    };

    const getUsageCount = (style) => {
        if (typeof style.usageCount === 'number') {
            return style.usageCount;
        }
        return 0;
    };

    const compareByUsage = (a, b, order = 'desc') => {
        const usageA = getUsageCount(a);
        const usageB = getUsageCount(b);
        if (usageA === usageB) return 0;
        return order === 'asc' ? usageA - usageB : usageB - usageA;
    };

    // Computed values for map styles pagination - using filtered defaultStyles
    const sortedStyles = [...defaultStyles].sort((a, b) => {
        // If both have same priority (both in list or both not in list), apply secondary sorting
        if (styleSortBy === 'name') {
            const nameA = a.name.toLowerCase();
            const nameB = b.name.toLowerCase();
            const nameComparison = styleSortOrder === 'asc'
                ? nameA.localeCompare(nameB)
                : nameB.localeCompare(nameA);
            if (nameComparison !== 0) {
                return nameComparison;
            }
            return compareByUsage(a, b, styleSortOrder);
        } else if (styleSortBy === 'recommended') {
            const aIndex = recommendedStyles.findIndex(style => style.id === a.id);
            const bIndex = recommendedStyles.findIndex(style => style.id === b.id);

            if (aIndex !== -1 && bIndex === -1) return -1;
            if (aIndex === -1 && bIndex !== -1) return 1;
            if (aIndex !== -1 && bIndex !== -1) return aIndex - bIndex;
            const usageFallback = compareByUsage(a, b, styleSortOrder);
            if (usageFallback !== 0) return usageFallback;
        }

        const usageComparison = compareByUsage(a, b, styleSortOrder);
        if (usageComparison !== 0) {
            return usageComparison;
        }

        const priorityA = getStylePriority(a.name);
        const priorityB = getStylePriority(b.name);

        if (priorityA !== priorityB) {
            return priorityA - priorityB;
        }

        const createdAtA = a.createdAt?.toDate?.() || (a.createdAt instanceof Date ? a.createdAt : new Date(0));
        const createdAtB = b.createdAt?.toDate?.() || (b.createdAt instanceof Date ? b.createdAt : new Date(0));

        return styleSortOrder === 'asc'
            ? createdAtA - createdAtB
            : createdAtB - createdAtA;
    });

    const styleStartIndex = (currentStylePage - 1) * itemsPerPage;
    const styleEndIndex = styleStartIndex + itemsPerPage;
    const currentStyles = sortedStyles.slice(styleStartIndex, styleEndIndex);
    const totalStylePages = Math.ceil(sortedStyles.length / itemsPerPage);

    // Add hero image rotation
    useEffect(() => {
        // Preload logos
        [urbanViceLogo, mythicLogo, japaneseLogo, classicRtsLogo, modernVueltaLogo, logo80sTdF, logoAboriginal, logoAztec, logoCaravanAtlas].forEach(logoSrc => {
            const img = new Image();
            img.src = logoSrc;
        });

        const fetchHeroImages = async () => {
            try {
                // This would be a call to your backend to get hero images
                // For now, using placeholder images
                const images = [
                    'https://cdn.builder.io/api/v1/image/assets/TEMP/b875398bec55a60186a30ebc0cda9ca6d86290e7',
                    // Add more hero images as they become available
                ];
                setHeroImages(images);
            } catch (error) {
                console.error('Error fetching hero images:', error);
            }
        };

        fetchHeroImages();

        // Rotate hero images every 10 seconds
        if (heroImages.length > 1) {
            const interval = setInterval(() => {
                setCurrentHeroIndex(prev => (prev + 1) % heroImageConfigs.length);
            }, 10000);
            return () => {
                clearInterval(interval);
            }
        }
    }, [heroImages.length]);

    // Add function to fetch prompt templates
    const fetchPromptTemplates = useCallback(async () => {
        if (!user) return;
        setIsLoadingTemplates(true);
        try {
            const getTemplatesFunc = httpsCallable(functions, 'getPromptTemplates');
            const result = await getTemplatesFunc();
            if (result.data && Array.isArray(result.data.templates)) {
                console.log("Raw template data from Firebase:", result.data.templates);
                if (result.data.templates.length > 0) {
                    console.log("First template structure:", result.data.templates[0]);
                    console.log("First template id:", result.data.templates[0].id);
                    console.log("Template usage snapshot:", result.data.templates.map(t => ({ name: t.name, usageCount: t.usageCount || 0 })));
                }
                const normalizedTemplates = result.data.templates.map((template) => ({
                    ...template,
                    mapInputType: template.mapInputType || '2d',
                    variantOf: template.variantOf || null,
                }));
                setPromptTemplates(normalizedTemplates);
            } else {
                setPromptTemplates([]);
            }
        } catch (error) {
            console.error("Error fetching prompt templates:", error);
            setPromptTemplates([]);
        } finally {
            setIsLoadingTemplates(false);
        }
    }, [user]);

    // Effect to check Strava connection and load data when user prop changes
    useEffect(() => {
        if (user) {
            checkAndLoadStravaData(user.uid);
            fetchPromptTemplates();
            loadMonthlyUsage();
        } else {
            setStravaConnected(false);
            setActivities([]);
            setIsLoadingActivities(false);
            setPromptTemplates([]);
            setIsLoadingUsage(false);
            setSelectedTemplate(null);
            setSelectedTemplateGroup(null);
        }
    }, [user, fetchPromptTemplates]);

    const posthog = usePostHog();

    const handleStravaConnect = () => {
        // Track Strava connection attempt
        posthog?.capture('strava_connect_clicked', {
            user_authenticated: !!user,
            page: 'landing'
        });

        if (!STRAVA_CLIENT_ID || STRAVA_CLIENT_ID === "YOUR_STRAVA_CLIENT_ID") {
            alert("Strava Client ID placeholder is still in use. Please update it.");
            return;
        }

        const state = Math.random().toString(36).substring(2);
        localStorage.setItem('stravaOAuthState', state);

        const consentUrl = `https://www.strava.com/oauth/authorize?client_id=${STRAVA_CLIENT_ID}&redirect_uri=${encodeURIComponent(STRAVA_SIGNIN_REDIRECT_URI)}&response_type=code&scope=${encodeURIComponent(STRAVA_SCOPE)}&approval_prompt=auto&state=${state}`;
        window.location.href = consentUrl;
    };

    const checkAndLoadStravaData = async (userId) => {
        setIsLoadingActivities(true);
        try {
            const tokenDocRef = doc(db, 'users', userId, 'stravaTokens', 'tokens');
            const docSnap = await getDoc(tokenDocRef);
            if (docSnap.exists()) {
                setStravaConnected(true);
                const getActivitiesFunc = httpsCallable(functions, 'getStravaActivities');
                const result = await getActivitiesFunc();
                if (result.data) {
                    setActivities(Array.isArray(result.data) ? result.data : []);
                } else {
                    setActivities([]);
                    console.warn("No activity data returned from getStravaActivities function.");
                }
            } else {
                setStravaConnected(false);
                setActivities([]);
            }
        } catch (error) {
            console.error("Error checking Strava connection or loading activities:", error);
            setStravaConnected(false);
            setActivities([]);
        } finally {
            setIsLoadingActivities(false);
        }
    };

    // Keep the listener for Strava success messages
    useEffect(() => {
        const handleStravaSuccess = (event) => {
            if (event.origin !== window.location.origin || event.data !== "stravaAuthSuccess") return;
            if (user) checkAndLoadStravaData(user.uid);
            posthog?.capture('strava_connected_success', {
                provider: 'strava'
            });
        };
        window.addEventListener("message", handleStravaSuccess);
        return () => window.removeEventListener("message", handleStravaSuccess);
    }, [user, posthog]);

    const handleSortChange = (newSortBy) => {
        if (newSortBy === sortBy) {
            setSortOrder(currentOrder => currentOrder === 'asc' ? 'desc' : 'asc');
        } else {
            setSortBy(newSortBy);
            setSortOrder(newSortBy === 'name' ? 'asc' : 'desc');
        }
        setCurrentPage(1); // Reset to first page when sorting changes
    };

    const goToNextPage = () => {
        if (currentPage < totalPages) {
            setCurrentPage(prev => prev + 1);
            // Scroll to the top of the activities section
            setTimeout(() => {
                document.getElementById('activitiesSection')?.scrollIntoView({ behavior: 'smooth' });
            }, 100);
        }
    };

    const goToPreviousPage = () => {
        if (currentPage > 1) {
            setCurrentPage(prev => prev - 1);
            // Scroll to the top of the activities section
            setTimeout(() => {
                document.getElementById('activitiesSection')?.scrollIntoView({ behavior: 'smooth' });
            }, 100);
        }
    };

    const handleStyleSortChange = (newSortBy) => {
        if (newSortBy === styleSortBy) {
            setStyleSortOrder(currentOrder => currentOrder === 'asc' ? 'desc' : 'asc');
        } else {
            setStyleSortBy(newSortBy);
            setStyleSortOrder(newSortBy === 'name' ? 'asc' : 'desc');
        }
        setCurrentStylePage(1); // Reset to first page when sorting changes
    };

    const goToNextStylePage = () => {
        if (currentStylePage < totalStylePages) {
            setCurrentStylePage(prev => prev + 1);
        }
    };

    const goToPreviousStylePage = () => {
        if (currentStylePage > 1) {
            setCurrentStylePage(prev => prev - 1);
        }
    };

    // Helper to format duration from seconds
    const formatDuration = (totalSeconds) => {
        if (isNaN(totalSeconds) || totalSeconds < 0) return 'N/A';
        const hours = Math.floor(totalSeconds / 3600);
        const minutes = Math.floor((totalSeconds % 3600) / 60);
        const seconds = Math.floor(totalSeconds % 60);

        const pad = (num) => String(num).padStart(2, '0');

        if (hours > 0) {
            return `${hours}:${pad(minutes)}:${pad(seconds)}`;
        }
        return `${pad(minutes)}:${pad(seconds)}`;
    };

    // Update handleActivitySelect to expand styles section when activity is selected
    const handleActivitySelect = (activity) => {
        // Check if activity has map data
        if (!activity.map || !activity.map.summary_polyline) {
            // Don't select activities without map data
            return;
        }

        setSelectedActivity(activity);
        // Expand styles section and collapse activities section when activity is selected
        setIsStylesSectionExpanded(true);
        setIsActivitiesSectionExpanded(false);
        // Scroll to styles section if template is already selected
        if (selectedTemplate) {
            setTimeout(() => {
                document.getElementById('styles-section')?.scrollIntoView({ behavior: 'smooth' });
            }, 300); // Wait for animation
        } else {
            setTimeout(() => {
                document.getElementById('styles-section')?.scrollIntoView({ behavior: 'smooth' });
            }, 300);
        }
    };

    const handleTemplateVariantSelect = useCallback((variant, { skipNavigation = false } = {}) => {
        if (!variant) {
            return;
        }

        const variantType = (variant.mapInputType || '2d');
        const isVariant3D = variantType === '3d';

        clear3DProgressTimer();

        setSelectedTemplate(variant);
        setUse3DMap(isVariant3D);

        if (isVariant3D) {
            setIs3DSelectionPending(true);
            setPending3DVariantId(variant.id || null);
            setThreeDRenderProgress(5);
        } else {
            setIs3DSelectionPending(false);
            setPending3DVariantId(null);
            setThreeDRenderProgress(0);
        }
        if (!skipNavigation) {
            setIsStylesSectionExpanded(false);
            if (selectedActivity) {
                setTimeout(() => {
                    document.getElementById('build-myth-section')?.scrollIntoView({ behavior: 'smooth' });
                }, isMobile ? 400 : 300);
            }
        }
    }, [clear3DProgressTimer, selectedActivity, isMobile]);

    // Update handleTemplateSelect to expand build section and collapse styles section when template is selected
    const handleTemplateSelect = (templateGroup) => {
        if (!templateGroup) {
            return;
        }

        console.log("Selected template group:", templateGroup);

        const variants = templateGroup.variants || [];
        const previouslySelectedVariant = selectedTemplate && variants.find((variant) => variant.id === selectedTemplate.id);
        const defaultVariant = previouslySelectedVariant
            || variants.find((variant) => (variant.mapInputType || '2d') === '2d')
            || variants[0];

        setSelectedTemplateGroup(templateGroup);
        if (defaultVariant) {
            handleTemplateVariantSelect(defaultVariant, { skipNavigation: isMobile && templateGroup?.variants?.some((variant) => (variant.mapInputType || '2d') === '3d') });
        } else {
            setSelectedTemplate(null);
            setUse3DMap(false);
            setIs3DSelectionPending(false);
            setPending3DVariantId(null);
            setThreeDRenderProgress(0);
        }

        const has3DVariant = templateGroup?.variants?.some((variant) => (variant.mapInputType || '2d') === '3d');
        const shouldFocusVariants = isMobile && has3DVariant && (templateGroup?.variants?.length || 0) > 1;

        if (shouldFocusVariants) {
            setIsStylesSectionExpanded(true);
            setTimeout(() => {
                document.getElementById('style-variant-selector')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }, 300);
        } else {
            setIsStylesSectionExpanded(false);
            if (selectedActivity) {
                setTimeout(() => {
                    document.getElementById('build-myth-section')?.scrollIntoView({ behavior: 'smooth' });
                }, 300);
            }
        }
    };

    // Add toggle functions
    const toggleActivitiesSection = () => {
        setIsActivitiesSectionExpanded(!isActivitiesSectionExpanded);
    };

    const toggleStylesSection = () => {
        setIsStylesSectionExpanded(!isStylesSectionExpanded);
    };

    // Handle generation submission
    const handleGenerateMap = async () => {
        const shouldUse3DMap = is3DVariantSelected || use3DMap;
        posthog?.capture('map_generation_started', {
            activity_id: selectedActivity?.id,
            template_id: selectedTemplate?.id,
            style: selectedTemplate?.name || selectedTemplate?.id,
            user_id: user?.uid,
            is_3d_map: shouldUse3DMap,
            map_input_type: selectedTemplate?.mapInputType || '2d'
        });

        if (!user || !selectedActivity || !selectedTemplate) return;

        // If 3D map is enabled, capture the current view automatically
        let captured3DMapUploadPath = null;
        if (shouldUse3DMap) {
            const selectedActivityId = selectedActivity.id?.toString() || selectedActivity.id;
            let captureFunc = null;

            if (active3DMapRef.current.activityId === selectedActivityId && active3DMapRef.current.captureFunc) {
                captureFunc = active3DMapRef.current.captureFunc;
            }

            if (!captureFunc) {
                captureFunc = captureMapCallbacksRef.current[selectedActivityId];
            }

            if (!captureFunc) {
                setStatusError('3D map is not ready to capture yet. Please wait for it to load.');
                return;
            }

            setStatusError('');
            try {
                const captureResult = await captureFunc();
                if (!captureResult || !captureResult.blob) {
                    setStatusError('Failed to capture 3D map. Please try again.');
                    return;
                }

                if (captureResult.dataUrl) {
                    setCaptured3DImage(captureResult.dataUrl);
                }

                const timestamp = Date.now();
                const pathExtension = captureResult.mimeType === 'image/jpeg' ? 'jpg' : 'png';
                const storagePath = `composite-maps/${user.uid}/captured-3d/${selectedActivityId}_${timestamp}.${pathExtension}`;
                const storageRef = ref(storage, storagePath);

                await uploadBytes(storageRef, captureResult.blob, {
                    contentType: captureResult.mimeType || 'image/png'
                });

                captured3DMapUploadPath = storagePath;
            } catch (error) {
                console.error('Error capturing 3D map:', error);
                setStatusError('Error capturing 3D map. Please try again.');
                return;
            } finally {
                setTimeout(() => setStatusError(''), 2000);
            }
        }

        // Check monthly limit before proceeding
        if (monthlyUsage.remaining <= 0) {
            setStatusError(`You have run out of credits (${monthlyUsage.currentCount}/${monthlyUsage.monthlyLimit}).`);
            posthog?.capture('out_of_credits', {
                current_count: monthlyUsage.currentCount,
                monthly_limit: monthlyUsage.monthlyLimit,
                context: 'pre_generation_check',
                timestamp: new Date().toISOString()
            });
            return;
        }

        console.log("=== Generation Debug ===");
        console.log("Selected activity:", selectedActivity);
        console.log("Selected template:", selectedTemplate);
        console.log("Template ID being sent:", selectedTemplate.id);
        console.log("Activity ID being sent:", selectedActivity.id);
        console.log("Monthly usage:", monthlyUsage);

        setIsGenerating(true);
        setStatusError('');

        try {
            // Use the proper server-side function that includes limit checking
            const requestImageGenerationFunc = httpsCallable(functions, 'requestImageGeneration');
            
            const requestPayload = {
                StravaId: selectedActivity.id.toString(),
                promptTemplateId: selectedTemplate.id,
                gpxDataSource: 'polyline',
                mapboxStyle: 'mapbox://styles/mapbox/streets-v12', // Default Mapbox style
                // Include captured 3D map storage path if available
                ...(captured3DMapUploadPath && { captured3DMapPath: captured3DMapUploadPath }),
                use3DMap: shouldUse3DMap,
                mapInputType: selectedTemplate.mapInputType || '2d',
                // Include Strava stats for the generation pipeline
                stravaStats: {
                    activityName: selectedActivity.name,
                    activityType: selectedActivity.type,
                    distance: selectedActivity.distance,
                    movingTime: selectedActivity.moving_time,
                    elevationGain: selectedActivity.total_elevation_gain,
                    averageSpeed: selectedActivity.average_speed,
                    averageWatts: selectedActivity.average_watts !== undefined ? selectedActivity.average_watts : null,
                    activityDate: { seconds: new Date(selectedActivity.start_date).getTime() / 1000 }
                }
            };

            const result = await requestImageGenerationFunc(requestPayload);
            console.log("Server response:", result.data);
            
            // Navigate to the map page with the request ID
            navigate(`/map/${result.data.requestId}`);
            
        } catch (e) {
            console.error("Error submitting request:", e);

            if (captured3DMapUploadPath) {
                try {
                    await deleteObject(ref(storage, captured3DMapUploadPath));
                    console.warn('Cleaned up temporary 3D capture after failure');
                } catch (cleanupError) {
                    console.warn('Failed to clean up temporary 3D capture:', cleanupError);
                }
            }

            // Handle specific error types
            if (e.code === 'functions/resource-exhausted') {
                setStatusError("You have run out of credits.");
                posthog?.capture('out_of_credits', {
                    current_count: monthlyUsage.currentCount,
                    monthly_limit: monthlyUsage.monthlyLimit,
                    context: 'generation_error',
                    error_code: e.code,
                    timestamp: new Date().toISOString()
                });
                // Reload monthly usage to get current count
                loadMonthlyUsage();
            } else if (e.code === 'functions/already-exists') {
                // Handle concurrent generation error
                const details = e.details;
                if (details && details.existingRequestId) {
                    // Navigate to the in-progress generation
                    navigate(`/map/${details.existingRequestId}`);
                } else {
                    setStatusError("You already have a map being generated. Please wait for it to complete before starting a new one.");
                }
                posthog?.capture('concurrent_generation_blocked', {
                    context: 'landing_page',
                    activity_id: selectedActivity?.id,
                    timestamp: new Date().toISOString()
                });
            } else {
                setStatusError(`Error: ${e.message}`);
            }
            setIsGenerating(false);
        }
    };

    // Monitor request status - removed since we navigate to share page immediately

    // Effect to control navigation visibility based on user state
    useEffect(() => {
        if (setShowNavigation) {
            setShowNavigation(!!user); // Show nav only when user is logged in
        }
        
        // Cleanup: ensure nav is shown when component unmounts
        return () => {
            if (setShowNavigation) {
                setShowNavigation(true);
            }
        };
    }, [user, setShowNavigation]);

    // Update the hero image rotation effect
    useEffect(() => {
        let isActive = true;

        if (!heroAnimationEnabled) {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
                intervalRef.current = null;
            }
            setIsAnimating(false);
            setIsTransitioning(false);
            setIsLoadingHeroImage(false);
            return () => {
                isActive = false;
            };
        }

        const fetchHeroImageData = async (config) => {
            setIsLoadingHeroImage(true);
            try {
                if (config?.staticImages?.original && config?.staticImages?.generated) {
                    const { original, generated, activityName } = config.staticImages;
                    const preloadPromises = [
                        new Promise((resolve, reject) => {
                            const img = new Image();
                            img.onload = resolve;
                            img.onerror = reject;
                            img.src = original;
                        }),
                        new Promise((resolve, reject) => {
                            const img = new Image();
                            img.onload = resolve;
                            img.onerror = reject;
                            img.src = generated;
                        })
                    ];
                    await Promise.all(preloadPromises);
                    if (!isActive) return;
                    setHeroImageData({
                        originalImageUrl: original,
                        generatedImageUrl: generated,
                        activityName: activityName || 'Epic Adventure'
                    });
                } else if (config?.id) {
                    const docRef = doc(db, "GenerationRequests", config.id);
                    const docSnap = await getDoc(docRef);
                    if (docSnap.exists()) {
                        const data = docSnap.data();
                        let originalImageUrl = null;
                        let generatedImageUrl = null;
                        if (data.originalImagePath) {
                            try {
                                originalImageUrl = await getDownloadURL(ref(storage, data.originalImagePath));
                            } catch (error) {
                                console.warn('Could not load original image:', error);
                            }
                        }
                        if (data.generatedImagePath) {
                            try {
                                generatedImageUrl = await getDownloadURL(ref(storage, data.generatedImagePath));
                            } catch (error) {
                                console.warn('Could not load generated image:', error);
                            }
                        }
                        if (originalImageUrl && generatedImageUrl) {
                            const preloadPromises = [
                                new Promise((resolve, reject) => {
                                    const img = new Image();
                                    img.onload = resolve;
                                    img.onerror = reject;
                                    img.src = originalImageUrl;
                                }),
                                new Promise((resolve, reject) => {
                                    const img = new Image();
                                    img.onload = resolve;
                                    img.onerror = reject;
                                    img.src = generatedImageUrl;
                                })
                            ];
                            await Promise.all(preloadPromises);
                            if (!isActive) return;
                            setHeroImageData({
                                originalImageUrl,
                                generatedImageUrl,
                                activityName: data.stravaStats?.activityName || 'Epic Adventure'
                            });
                        }
                    }
                }
            } catch (error) {
                console.error('Error fetching hero image data:', error);
            } finally {
                if (isActive) {
                    setIsLoadingHeroImage(false);
                }
            }
        };

        const startAutoRotation = () => {
            console.log('[Hero Rotation] Starting auto-rotation at', new Date().toLocaleTimeString());

            if (intervalRef.current) {
                clearInterval(intervalRef.current);
            }

            intervalRef.current = setInterval(() => {
                if (!isActive) {
                    return;
                }
                console.log('[Hero Rotation] Auto-rotating to next image at', new Date().toLocaleTimeString());
                setCurrentHeroIndex(currentIdx => {
                    const nextIdx = (currentIdx + 1) % heroImageConfigs.length;

                    setIsTransitioning(true);

                    preloadHeroImage(heroImageConfigs[nextIdx]).then(nextImageData => {
                        if (!isActive) {
                            return;
                        }
                        if (nextImageData) {
                            transitionToNextImage(nextImageData);
                        } else {
                            setIsTransitioning(false);
                        }
                    });

                    return nextIdx;
                });
            }, 10000); // Changed from 25000 to 10000 (10 seconds)
        };

        fetchHeroImageData(heroImageConfigs[0]);
        startAutoRotation();

        return () => {
            isActive = false;
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
                intervalRef.current = null;
            }
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [heroAnimationEnabled]);

    // Navigation functions for hero images
    const goToNextHeroImage = async (e) => {
        e?.preventDefault();

        if (!heroAnimationEnabled) {
            return;
        }

        // Immediately start fade-out effect
        setIsTransitioning(true);

        // Use functional update to get current index and calculate next
        setCurrentHeroIndex(currentIdx => {
            const nextIdx = (currentIdx + 1) % heroImageConfigs.length;

            // Preload and transition to next image
            preloadHeroImage(heroImageConfigs[nextIdx]).then(nextImageData => {
                if (nextImageData) {
                    transitionToNextImage(nextImageData);
                } else {
                    // If preload failed, reset transition state
                    setIsTransitioning(false);
                }
            });

            return nextIdx;
        });

        // Restart auto-rotation from this point
        if (intervalRef.current) clearInterval(intervalRef.current);
        intervalRef.current = setInterval(() => {
            setCurrentHeroIndex(currentIdx => {
                const nextIdx = (currentIdx + 1) % heroImageConfigs.length;

                // Start fade-out immediately
                setIsTransitioning(true);

                preloadHeroImage(heroImageConfigs[nextIdx]).then(nextImageData => {
                    if (nextImageData) {
                        transitionToNextImage(nextImageData);
                    } else {
                        setIsTransitioning(false);
                    }
                });

                return nextIdx;
            });
        }, 10000); // Changed from 25000 to 10000 (10 seconds)
    };

    const goToPreviousHeroImage = async (e) => {
        e?.preventDefault();
        
        if (!heroAnimationEnabled) {
            return;
        }

        // Immediately start fade-out effect
        setIsTransitioning(true);
        
        // Use functional update to get current index and calculate previous
        setCurrentHeroIndex(currentIdx => {
            const prevIndex = currentIdx === 0 ? heroImageConfigs.length - 1 : currentIdx - 1;
            
            // Preload and transition to previous image
            preloadHeroImage(heroImageConfigs[prevIndex]).then(prevImageData => {
                if (prevImageData) {
                    transitionToNextImage(prevImageData);
                } else {
                    // If preload failed, reset transition state
                    setIsTransitioning(false);
                }
            });
            
            return prevIndex;
        });
        
        // Restart auto-rotation from this point
        if (intervalRef.current) clearInterval(intervalRef.current);
        intervalRef.current = setInterval(() => {
            setCurrentHeroIndex(currentIdx => {
                const nextIdx = (currentIdx + 1) % heroImageConfigs.length;
                
                // Start fade-out immediately
                setIsTransitioning(true);
                
                preloadHeroImage(heroImageConfigs[nextIdx]).then(nextImageData => {
                    if (nextImageData) {
                        transitionToNextImage(nextImageData);
                    } else {
                        setIsTransitioning(false);
                    }
                });
                return nextIdx;
            });
        }, 10000); // Changed from 25000 to 10000 (10 seconds)
    };

    // Modify the automatic slider animation effect to trigger on each new image
    useEffect(() => {
        if (!heroAnimationEnabled || !heroImageData) {
            return;
        }

        console.log('Animation effect triggered, heroImageData:', !!heroImageData);

        setIsAnimating(false);
        setSliderPosition(90);
        updateClipPath(90);

        let animationFrameId = null;

        const startDelay = setTimeout(() => {
            if (!heroAnimationEnabled) {
                return;
            }

            console.log('Starting slider animation');
            setIsAnimating(true);

            let animationFrame = 0;
            const totalFrames = 180; // 3 seconds at 60fps

            const animate = () => {
                if (!heroAnimationEnabled) {
                    setIsAnimating(false);
                    return;
                }

                if (animationFrame < totalFrames) {
                    const progress = animationFrame / totalFrames;

                    let newPosition;
                    if (progress <= 0.5) {
                        newPosition = 90 - (progress * 2 * 90);
                    } else {
                        newPosition = (progress - 0.5) * 2 * 80;
                    }

                    setSliderPosition(newPosition);

                    animationFrame++;
                    animationFrameId = requestAnimationFrame(animate);
                } else {
                    console.log('Animation completed');
                    setSliderPosition(80);
                    setIsAnimating(false);
                }
            };

            animationFrameId = requestAnimationFrame(animate);
        }, 3000);

        return () => {
            clearTimeout(startDelay);
            if (animationFrameId) {
                cancelAnimationFrame(animationFrameId);
            }
            setIsAnimating(false);
        };
    }, [heroAnimationEnabled, heroImageData]);

    // Image comparison slider handlers - EXACTLY like SharePage
    const updateClipPath = (percentage) => {
        // Update desktop hero image comparison
        const topImage = document.querySelector('.hero-image-comparison .top-image');
        if (topImage) {
            topImage.style.clipPath = `polygon(0 0, 100% 0, 100% ${percentage}%, 0 ${percentage}%)`;
        }
        
        // Update mobile image comparison
        const mobileTopImage = document.querySelector('.mobile-image-comparison .image-comparison-container .top-image');
        if (mobileTopImage) {
            mobileTopImage.style.clipPath = `polygon(0 0, 100% 0, 100% ${percentage}%, 0 ${percentage}%)`;
        }
        
        // Update slider line position for both desktop and mobile
        const desktopSliderLine = document.querySelector('.hero-image-comparison .slider-line');
        if (desktopSliderLine) {
            desktopSliderLine.style.top = `${percentage}%`;
        }
        
        const mobileSliderLine = document.querySelector('.mobile-image-comparison .slider-line');
        if (mobileSliderLine) {
            mobileSliderLine.style.top = `${percentage}%`;
        }
    };

    const handleSliderChange = (event) => {
        const newPosition = parseInt(event.target.value);
        setSliderPosition(newPosition);
    };

    const startDrag = (e) => {
        setIsAnimating(false); // Stop animation on user interaction
        isDraggingRef.current = true;
        
        // Prevent default behavior for all touch events
        if (e.type === 'touchstart') {
            e.preventDefault();
            e.stopPropagation();
        }
        
        onDrag(e);
    };
    
    const onDrag = (e) => {
        if (!isDraggingRef.current) return; // Removed isAnimating check to allow interaction
        
        // Prevent default behavior and stop propagation for touch events
        if (e.type === 'touchmove') {
            e.preventDefault();
            e.stopPropagation();
        }
        
        // Handle touch events
        const clientY = e.touches ? e.touches[0].clientY : e.clientY;
        
        // Check for both desktop and mobile containers
        let container = e.currentTarget?.closest('.hero-image-comparison') || 
                       e.currentTarget?.closest('.image-comparison-container');
        
        // If called from document event listener, find the container manually
        if (!container) {
            container = document.querySelector('.hero-image-comparison') || 
                       document.querySelector('.image-comparison-container');
        }
        
        if (!container) return;
        
        const rect = container.getBoundingClientRect();
        const y = clientY - rect.top;
        const percentage = Math.max(0, Math.min(100, (y / rect.height) * 100));
        
        setSliderPosition(percentage);
        // Call updateClipPath immediately for responsive dragging
        updateClipPath(percentage);
    };
    
    const stopDrag = () => { 
        isDraggingRef.current = false; 
    };

    // Update clip path when slider position changes (only for animation, not user interaction)
    useEffect(() => {
        if (!heroAnimationEnabled) {
            return;
        }
        if (!isDraggingRef.current && isAnimating) {
            updateClipPath(sliderPosition);
        }
    }, [sliderPosition, heroImageData, isAnimating, heroAnimationEnabled]);

    // Set initial clip path when hero image data loads
    useEffect(() => {
        if (!heroAnimationEnabled || !heroImageData) {
            return;
        }
        updateClipPath(80);
    }, [heroAnimationEnabled, heroImageData]);

    // Add proper touch event handling to prevent page scrolling
    useEffect(() => {
        if (!heroAnimationEnabled || !heroImageData) {
            return;
        }

        const handleTouchMove = (e) => {
            if (isDraggingRef.current) {
                e.preventDefault();
                e.stopPropagation();
                onDrag(e);
            }
        };

        const handleTouchEnd = (e) => {
            if (isDraggingRef.current) {
                e.preventDefault();
                e.stopPropagation();
            }
            stopDrag();
        };

        const handleTouchStart = (e) => {
            // Only prevent default if we're starting a drag on the slider
            const target = e.target;
            const isSliderElement = target.closest('.image-comparison-container') || 
                                  target.closest('.hero-image-comparison');
            
            if (isSliderElement) {
                e.preventDefault();
                e.stopPropagation();
            }
        };

        // Add touch event listeners with passive: false to allow preventDefault
        // But only add them to the specific hero elements, not the entire document
        const heroElements = document.querySelectorAll('.hero-image-comparison, .image-comparison-container');

        heroElements.forEach(element => {
            element.addEventListener('touchstart', handleTouchStart, { passive: false });
            element.addEventListener('touchmove', handleTouchMove, { passive: false });
            element.addEventListener('touchend', handleTouchEnd, { passive: false });
        });

        return () => {
            heroElements.forEach(element => {
                element.removeEventListener('touchstart', handleTouchStart);
                element.removeEventListener('touchmove', handleTouchMove);
                element.removeEventListener('touchend', handleTouchEnd);
            });
        };
    }, [heroAnimationEnabled, heroImageData]);

    // Function to preload and prepare next hero image
    const preloadHeroImage = async (config) => {
        try {
            // Prefer static images if provided on the config
            if (config?.staticImages?.original && config?.staticImages?.generated) {
                const { original, generated, activityName } = config.staticImages;
                const preloadPromises = [
                    new Promise((resolve, reject) => {
                        const img = new Image();
                        img.onload = resolve;
                        img.onerror = reject;
                        img.src = original;
                    }),
                    new Promise((resolve, reject) => {
                        const img = new Image();
                        img.onload = resolve;
                        img.onerror = reject;
                        img.src = generated;
                    })
                ];
                await Promise.all(preloadPromises);
                return {
                    originalImageUrl: original,
                    generatedImageUrl: generated,
                    activityName: activityName || 'Epic Adventure'
                };
            }

            if (config?.id) {
                const docRef = doc(db, "GenerationRequests", config.id);
                const docSnap = await getDoc(docRef);
                if (docSnap.exists()) {
                    const data = docSnap.data();
                    let originalImageUrl = null;
                    let generatedImageUrl = null;
                    if (data.originalImagePath) {
                        try {
                            originalImageUrl = await getDownloadURL(ref(storage, data.originalImagePath));
                        } catch (error) {
                            console.warn('Could not load original image:', error);
                            return null;
                        }
                    }
                    if (data.generatedImagePath) {
                        try {
                            generatedImageUrl = await getDownloadURL(ref(storage, data.generatedImagePath));
                        } catch (error) {
                            console.warn('Could not load generated image:', error);
                            return null;
                        }
                    }
                    if (originalImageUrl && generatedImageUrl) {
                        const preloadPromises = [
                            new Promise((resolve, reject) => {
                                const img = new Image();
                                img.onload = resolve;
                                img.onerror = reject;
                                img.src = originalImageUrl;
                            }),
                            new Promise((resolve, reject) => {
                                const img = new Image();
                                img.onload = resolve;
                                img.onerror = reject;
                                img.src = generatedImageUrl;
                            })
                        ];
                        await Promise.all(preloadPromises);
                        return {
                            originalImageUrl,
                            generatedImageUrl,
                            activityName: data.stravaStats?.activityName || 'Epic Adventure'
                        };
                    }
                }
            }
        } catch (error) {
            console.error('Error preloading hero image data:', error);
        }
        return null;
    };

    // Function to transition to next image
    const transitionToNextImage = async (newImageData) => {
        if (!newImageData) return;
        
        // If transition hasn't started yet, start it now
        if (!isTransitioning) {
            setIsTransitioning(true);
            setNextHeroImageData(newImageData);
            
            // Wait a brief moment for the fade-out to start
            setTimeout(() => {
                setHeroImageData(newImageData);
                setNextHeroImageData(null);
                
                // Wait for fade-in to complete
                setTimeout(() => {
                    setIsTransitioning(false);
                }, 300); // Match CSS transition duration
            }, 150); // Half of transition duration for crossfade effect
        } else {
            // Transition already started, just update the image and fade back in
            setNextHeroImageData(newImageData);
            
            // Small delay to ensure the fade-out is visible
            setTimeout(() => {
                setHeroImageData(newImageData);
                setNextHeroImageData(null);
                
                // Wait for fade-in to complete
                setTimeout(() => {
                    setIsTransitioning(false);
                }, 300); // Match CSS transition duration
            }, 100); // Shorter delay since fade-out already started
        }
    };

    // Function to check if we have the user's first activity
    const isFirstActivity = activities.length === 0;

    const loadMonthlyUsage = async () => {
        if (!user) return;
        
        setIsLoadingUsage(true);
        try {
            const { httpsCallable } = await import("firebase/functions");
            const { functions } = await import("./firebase");
            const getUserMonthlyUsageFunc = httpsCallable(functions, 'getUserMonthlyUsage');
            const result = await getUserMonthlyUsageFunc();
            setMonthlyUsage(result.data);
        } catch (error) {
            console.error("Error loading monthly usage:", error);
            // Keep default values on error
        } finally {
            setIsLoadingUsage(false);
        }
    };



    // Render logged out state
    if (!user) {
        return (
            <div className="logged-out-container">
                <div className="login-section">
                    <div className="login-header">
                        <Link to="/" className="login-brand">Mythic Maps</Link>
                    </div>
                    <div className="login-content-wrapper">
                        <div className="login-content">
                            <div className="login-text-section">
                                <div className="hero-tagline">
                                    <img 
                                        src={heroImageConfigs[currentHeroIndex]?.logo} 
                                        alt={`Mythic Maps Logo`} 
                                        className="rotating-hero-logo"
                                    />
                                </div>
                                <h1 className="login-title">Welcome to Mythic Maps</h1>
                                <p className="login-subtitle">Connect with Strava to transform your routes into epic tales</p>
                            </div>
                            <div className="login-actions">
                                <button 
                                    className="strava-button-wrapper" 
                                    onClick={handleStravaConnect}
                                    disabled={isConnecting}
                                >
                                    {isConnecting ? (
                                        <div className="strava-button-content">
                                            <svg className="strava-icon" viewBox="0 0 24 24" fill="currentColor">
                                                <path d="M15.387 17.944l-2.089-4.116h-3.065L15.387 24l5.15-10.172h-3.066m-7.008-5.599l2.836 5.598h4.172L10.463 0l-7 13.828h4.169"/>
                                            </svg>
                                            Connecting...
                                        </div>
                                    ) : (
                                        <img src={stravaConnectButton} alt="Connect with Strava" className="strava-connect-btn-img" />
                                    )}
                                </button>
                            </div>

                            {/* Desktop image controller */}
                            <div className="desktop-image-controller">
                                <h3 className="image-controller-header">Explore Mythic Styles</h3>
                                {heroImageConfigs && (
                                    <div className="hero-nav-controls">
                                        <button 
                                            className="hero-nav-arrow hero-nav-prev" 
                                            onClick={goToPreviousHeroImage}
                                            disabled={isTransitioning}
                                        ></button>
                                        
                                        <div className="hero-progress-section">
                                            <div className="hero-progress-bar">
                                                <div 
                                                    className="hero-progress-fill" 
                                                    style={{ width: `${((currentHeroIndex + 1) / heroImageConfigs.length) * 100}%` }}
                                                ></div>
                                            </div>
                                            <span className="hero-progress-text">
                                                {currentHeroIndex + 1} of {heroImageConfigs.length}
                                            </span>
                                        </div>
                                        
                                        <button 
                                            className="hero-nav-arrow hero-nav-next" 
                                            onClick={goToNextHeroImage}
                                            disabled={isTransitioning}
                                        ></button>
                                    </div>
                                )}
                            </div>

                            {/* Mobile image comparison */}
                            <div className="mobile-image-comparison">
                                {/* Add the header directly in JSX instead of CSS ::before */}
                                <h3 className="mobile-image-controller-header">Explore Mythic Styles</h3>
                                
                                {/* Navigation controls */}
                                {heroImageConfigs && (
                                    <div className="hero-nav-controls">
                                        <button 
                                            className="hero-nav-arrow hero-nav-prev" 
                                            onClick={goToPreviousHeroImage}
                                            disabled={isTransitioning}
                                        ></button>
                                        
                                        <div className="hero-progress-section">
                                            <div className="hero-progress-bar">
                                                <div 
                                                    className="hero-progress-fill" 
                                                    style={{ width: `${((currentHeroIndex + 1) / heroImageConfigs.length) * 100}%` }}
                                                ></div>
                                            </div>
                                            <span className="hero-progress-text">
                                                {currentHeroIndex + 1} of {heroImageConfigs.length}
                                            </span>
                                        </div>
                                        
                                        <button 
                                            className="hero-nav-arrow hero-nav-next" 
                                            onClick={goToNextHeroImage}
                                            disabled={isTransitioning}
                                        ></button>
                                    </div>
                                )}

                                {/* Image content */}
                                {isLoadingHeroImage ? (
                                    <div className="hero-placeholder mobile">
                                    </div>
                                ) : heroImageData ? (
                                    <div className="image-comparison-wrapper">
                                        <div 
                                            className={`image-comparison-container ${isTransitioning ? 'transitioning' : ''}`}
                                            onMouseMove={onDrag}
                                            onMouseUp={stopDrag}
                                            onMouseLeave={stopDrag}
                                            onMouseDown={startDrag}
                                            onTouchMove={onDrag}
                                            onTouchEnd={stopDrag}
                                            onTouchStart={startDrag}
                                        >
                                            <img 
                                                src={heroImageData.originalImageUrl} 
                                                alt="Original map" 
                                                className="bottom-image"
                                            />
                                            <img 
                                                src={heroImageData.generatedImageUrl} 
                                                alt="Mythic map" 
                                                className="top-image"
                                            />
                                            <div 
                                                className="slider-line"
                                            ></div>
                                        </div>
                                    </div>
                                ) : null}
                            </div>
                        </div>
                    </div>
                    <div className="login-footer">
                        By continuing, you agree to our terms of service and privacy policy.
                    </div>
                </div>
                <div className="hero-section">
                    <div className="hero-wrapper">
                        {/* Remove nav controls from here and fix the duplicate className */}
                        <div 
                            className={`hero-image-comparison ${isTransitioning ? 'transitioning' : ''}`}
                            ref={heroContainerRef}
                            onMouseMove={onDrag}
                            onMouseUp={stopDrag}
                            onMouseLeave={stopDrag}
                            onMouseDown={startDrag}
                            onTouchStart={startDrag}
                            style={{ cursor: 'ns-resize' }}
                        >
                            {isLoadingHeroImage ? (
                                <div className="hero-placeholder"></div>
                            ) : heroImageData ? (
                                <>
                                    <img 
                                        src={heroImageData.originalImageUrl} 
                                        alt="Original map" 
                                        className="bottom-image"
                                    />
                                    <img 
                                        src={heroImageData.generatedImageUrl} 
                                        alt="Mythic map" 
                                        className="top-image"
                                    />
                                    <div 
                                        className="slider-line"
                                    ></div>
                                </>
                            ) : (
                                <div className="hero-fallback">
                                    <div className="hero-fallback-content">
                                        Mythic Maps
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    // Render logged in state (existing code)
    return (
        <div className="container">
            {isAdmin && (
                <div style={{
                    textAlign: 'right',
                    fontSize: '12px',
                    color: '#9CA3AF',
                    padding: '8px 0'
                }}>
                    Build: v2025.03.28-3d-map-rendering
                </div>
            )}
            {/* Home Section with Free Plan Card - Using SharePage layout */}
            <div className="share-header-row" style={{ marginBottom: '20px' }}>
                <div>
                    <h1 style={{
                        fontSize: '30px',
                        fontWeight: '500',
                        lineHeight: '1.27',
                        color: 'var(--Gray-900, #101828)',
                        margin: '0 0 4px 0'
                    }}>{t('landing.homeTitle', 'Home')}</h1>
                    <p className="history-subtitle">{t('landing.homeSubtitle', 'Welcome to your mythic journey')}</p>
                </div>
            </div>

            {/* Show PromoCard and CreditsCounter for logged in users */}
            {user ? (
                <div className="header-cards-container" style={{ marginBottom: '30px' }}>
                    <PromoCard 
                        user={user}
                        posthog={posthog}
                        variant="header"
                        onRewardClaimed={() => loadMonthlyUsage()}
                        style={{ flex: 1 }}
                    />
                    <CreditsCounter 
                        isLoadingUsage={isLoadingUsage}
                        monthlyUsage={monthlyUsage}
                        posthog={posthog}
                        page="home"
                        source="credits_card"
                    />
                </div>
            ) : (
                <div className="join-mythic-card" style={{ marginBottom: '30px' }}>
                    <div className="join-mythic-content">
                        <span className="join-mythic-text">Join Mythic Maps</span>
                        <button onClick={handleStravaConnect} className="strava-connect-button-wrapper">
                            <img src={stravaConnectButton} alt="Connect with Strava" className="strava-connect-btn-img-small" />
                        </button>
                    </div>
                </div>
            )}

            <div className="history-divider"></div>

            {/* Strava Activities Section */}
            {user && (
                <div id="activitiesSection" className="history-page collapsible-section" style={{ marginBottom: '40px' }}>
                    <div 
                        className="share-header-row strava-header collapsible-header" 
                        style={{ marginBottom: '20px', cursor: 'pointer' }}
                        onClick={toggleActivitiesSection}
                    >
                        <div>
                            <h2 style={{
                                fontSize: '30px',
                                fontWeight: '500',
                                lineHeight: '1.27',
                                color: 'var(--Gray-900, #101828)',
                                margin: '0 0 4px 0',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '12px'
                            }}>
                                1. {t('landing.importActivitiesTitle', 'Import your Strava Activities')}
                                <i className={`ti ${isActivitiesSectionExpanded ? 'ti-chevron-up' : 'ti-chevron-down'}`} 
                                   style={{ fontSize: '24px', color: 'var(--Gray-500)' }}></i>
                            </h2>
                            <p className="history-subtitle">
                                {selectedActivity ? 
                                    `${t('landing.selected', 'Selected')}: ${selectedActivity.name}` : 
                                    t('landing.importActivitiesSubtitle', 'Select your favourite Strava activities and make them Mythic')
                                }
                            </p>
                        </div>
                        {selectedActivity && (
                            <div className="selection-status">
                                <i className="ti ti-circle-check" style={{ fontSize: '24px', color: 'var(--Primary-600)' }}></i>
                            </div>
                        )}
                    </div>

                    <div className="history-divider"></div>

                    {/* Collapsed Activity Preview */}
                    {!isActivitiesSectionExpanded && selectedActivity && (
                        <div className="collapsed-activity-card">
                            <div className="activity-thumbnail">
                                <div className="activity-map-container">
                                    <div style={{ position: 'relative', width: '100%', height: '100%' }}>
                                            <div style={{
                                                position: 'relative',
                                                zIndex: 1
                                            }}>
                                            <ActivityMap
                                                activity={selectedActivity}
                                                width={collapsedPreviewWidth}
                                                height={collapsedPreviewHeight}
                                                onWaterAnalysis={handleWaterAnalysisCallback}
                                            />
                                        </div>
                                        {shouldRender3DMap && (
                                            <div style={{
                                                position: 'absolute',
                                                top: 0,
                                                left: 0,
                                                right: 0,
                                                bottom: 0,
                                                opacity: 0,
                                                pointerEvents: 'none'
                                            }}>
                                                <ActivityMap3D
                                                    activity={selectedActivity}
                                                    width={renderCaptureWidth}
                                                    height={renderCaptureHeight}
                                                    displayWidth={collapsedPreviewWidth}
                                                    displayHeight={collapsedPreviewHeight}
                                                    onCapture={(captureFunc) => {
                                                        if (selectedActivity?.id) {
                                                            captureMapCallbacksRef.current[selectedActivity.id.toString()] = captureFunc;
                                                        }
                                                    }}
                                                    onMapInteraction={(captureFunc) => {
                                                        if (selectedActivity?.id) {
                                                            active3DMapRef.current = {
                                                                activityId: selectedActivity.id.toString(),
                                                                captureFunc
                                                            };
                                                        }
                                                    }}
                                                    onRenderStart={handle3DRenderStart}
                                                    onRenderComplete={handle3DRenderComplete}
                                                    highDPI={false}
                                                    style="mapbox://styles/samuelcollin3333/cmf525thz00xg01rk0ewt0i6f"
                                                    bearing={0}
                                                    pitch={60}
                                                    lineWidth={8}
                                                />
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>

                            <div className="activity-details">
                                <div className="activity-title">
                                    <ActivityIcon type={selectedActivity.type} size={20} />
                                    <h3>{selectedActivity.name}</h3>
                                    {/* Admin badge */}
                                    {isAdmin && (
                                        <span style={{
                                            background: 'var(--Primary-600)',
                                            color: 'white',
                                            padding: '2px 6px',
                                            borderRadius: '3px',
                                            fontSize: '10px',
                                            fontWeight: '600',
                                            marginLeft: '8px'
                                        }}>
                                            ADMIN
                                        </span>
                                    )}
                                </div>

                                <div className="activity-stats">
                                    <div className="stat">
                                        <span className="stat-label">Distance</span>
                                        <span className="stat-value">
                                            {(selectedActivity.distance / 1000).toFixed(1)} km
                                        </span>
                                    </div>
                                    <div className="stat">
                                        <span className="stat-label">Elevation gain</span>
                                        <span className="stat-value">
                                            {selectedActivity.total_elevation_gain?.toFixed(0) || 'N/A'} m
                                        </span>
                                    </div>
                                    <div className="stat">
                                        <span className="stat-label">Time</span>
                                        <span className="stat-value">
                                            {formatDuration(selectedActivity.moving_time)}
                                        </span>
                                    </div>
                                    {/* Water percentage stat (toggled with Ctrl+W) */}
                                    {showWaterPercentages && activityWaterAnalysis[selectedActivity.id] && (
                                        <div className="stat" style={{ 
                                            background: 'rgba(74, 144, 226, 0.1)',
                                            borderRadius: '4px',
                                            padding: '4px 6px',
                                            border: '1px solid rgba(74, 144, 226, 0.3)'
                                        }}>
                                            <span className="stat-label" style={{ color: '#4A90E2', fontWeight: '500' }}>Water</span>
                                            <span className="stat-value" style={{ color: '#4A90E2' }}>
                                                {activityWaterAnalysis[selectedActivity.id].waterPercentage}%
                                            </span>
                                        </div>
                                    )}
                                    {selectedActivity.average_watts && (
                                        <div className="stat">
                                            <span className="stat-label">Power</span>
                                            <span className="stat-value">
                                                {selectedActivity.average_watts.toFixed(0)} W
                                            </span>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}

                    <div className={`collapsible-content ${isActivitiesSectionExpanded ? 'expanded' : 'collapsed'}`}>
                        {stravaConnected && activities.length > 0 && (
                            <div className="history-controls">
                                <div className="sort-options">
                                    <button
                                        onClick={() => handleSortChange('start_date')}
                                        className={sortBy === 'start_date' ? 'active' : ''}
                                    >
                                         {t('landing.sortByDate', 'Sort by date')}
                                    </button>
                                    <button
                                        onClick={() => handleSortChange('name')}
                                        className={sortBy === 'name' ? 'active' : ''}
                                    >
                                         {t('landing.sortByName', 'Sort by name')}
                                    </button>
                                    {/* Water percentage sorting (toggled with Ctrl+W) */}
                                    {showWaterPercentages && Object.keys(activityWaterAnalysis).length > 0 && (
                                        <button
                                            onClick={() => handleSortChange('water_percentage')}
                                            className={sortBy === 'water_percentage' ? 'active' : ''}
                                            style={{
                                                background: sortBy === 'water_percentage' ? 'var(--Primary-600)' : 'transparent',
                                                color: sortBy === 'water_percentage' ? 'white' : 'inherit',
                                                border: sortBy === 'water_percentage' ? '1px solid var(--Primary-600)' : '1px solid var(--Gray-300)',
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '4px'
                                            }}
                                        >
                                            <i className="ti ti-droplet" style={{ fontSize: '14px' }}></i>
                                            Sort by water %
                                        </button>
                                    )}

                            </div>
                                {/* Admin info about water analysis and photos */}
                                {isAdmin && (
                                    <div style={{
                                        fontSize: '12px',
                                        color: '#4A90E2',
                                        marginTop: '8px',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        gap: '4px'
                                    }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                            <i className="ti ti-info-circle" style={{ fontSize: '14px' }}></i>
                                            Admin: Water analysis shown for all activities
                                        </div>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                            <i className="ti ti-photo" style={{ fontSize: '14px' }}></i>
                                             {t('landing.activityPhotos', 'Activity photos')} {showActivityPhotos ? t('landing.shown','shown') : t('landing.hidden','hidden')} (Ctrl+P)
                                        </div>
                                    </div>
                                )}
                                {/* Info about water analysis (toggled with Ctrl+W) */}
                                {showWaterPercentages && (
                                    <div style={{
                                        fontSize: '12px',
                                        color: '#4A90E2',
                                        marginTop: '8px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '4px'
                                    }}>
                                        <i className="ti ti-info-circle" style={{ fontSize: '14px' }}></i>
                                         {t('landing.waterAnalysisShown','Water analysis shown for all activities')} (Ctrl+W)
                                    </div>
                                )}
                            </div>
                        )}

                        {isLoadingActivities ? (
                             <p style={{ textAlign: 'center' }}>{t('landing.loadingActivities','Loading activities...')}</p>
                        ) : activities.length > 0 ? (
                            <>
                                <ul className="history-requests-list">
                                    {currentActivities.map(activity => {
                                        const hasMapData = activity.map && activity.map.summary_polyline;
                                        return (
                                        <li
                                            key={activity.id}
                                            onClick={() => handleActivitySelect(activity)}
                                            className={`${selectedActivity?.id === activity.id ? 'selected-activity' : ''} ${!hasMapData ? 'no-map-activity' : ''}`}
                                             title={hasMapData ? t('landing.selectThisActivity','Select this activity') : t('landing.activityNoRoute','Activity without route data')}
                                            style={{ cursor: hasMapData ? 'pointer' : 'not-allowed' }}
                                        >
                                            <div className="history-thumbnail">
                                                {selectedActivity?.id === activity.id ? (
                                                    <div style={{ position: 'relative', width: '100%', height: '100%' }}>
                                                        <div style={{
                                                            position: 'relative',
                                                            zIndex: shouldShow3DPreview ? 0 : 1,
                                                            opacity: shouldShow3DPreview ? 0 : 1,
                                                            transition: 'opacity 0.3s ease',
                                                            pointerEvents: shouldShow3DPreview ? 'none' : 'auto'
                                                        }}>
                                                <ActivityMap
                                                    activity={activity}
                                                    width={listPreviewWidth}
                                                    height={listPreviewHeight}
                                                    onWaterAnalysis={handleWaterAnalysisCallback}
                                                />
                                                        </div>
                                                        {shouldRender3DMap && (
                                                            <div style={{
                                                                position: 'absolute',
                                                                top: 0,
                                                                left: 0,
                                                                right: 0,
                                                                bottom: 0,
                                                                opacity: 0,
                                                                pointerEvents: 'none'
                                                            }}>
                                                                <ActivityMap3D
                                                                    activity={activity}
                                                                    width={renderCaptureWidth}
                                                                    height={renderCaptureHeight}
                                                                    displayWidth={listPreviewWidth}
                                                                    displayHeight={listPreviewHeight}
                                                                    onCapture={(captureFunc) => {
                                                                        if (activity.id) {
                                                                            captureMapCallbacksRef.current[activity.id.toString()] = captureFunc;
                                                                        }
                                                                    }}
                                                                    onMapInteraction={(captureFunc) => {
                                                                        if (activity.id) {
                                                                            active3DMapRef.current = {
                                                                                activityId: activity.id.toString(),
                                                                                captureFunc
                                                                            };
                                                                        }
                                                                    }}
                                                                    highDPI={false}
                                                                    style="mapbox://styles/samuelcollin3333/cmf525thz00xg01rk0ewt0i6f"
                                                                    bearing={0}
                                                                    pitch={60}
                                                                    lineWidth={8}
                                                                />
                                                            </div>
                                                        )}
                                                    </div>
                                                ) : (
                                                    <ActivityMap
                                                        activity={activity}
                                                        width={LIST_MAP_WIDTH}
                                                        height={LIST_MAP_HEIGHT}
                                                        onWaterAnalysis={handleWaterAnalysisCallback}
                                                    />
                                                )}
                                            </div>
                                            <div className="history-details">
                                                <div className="activity-header">
                                                    <div className="activity-title-section">
                                                        <ActivityIcon type={activity.type} size={20} />
                                                        <h3 className="activity-name">{activity.name}</h3>
                                                        {/* Admin badge */}
                                                        {isAdmin && (
                                                            <span style={{
                                                                background: 'var(--Primary-600)',
                                                                color: 'white',
                                                                padding: '2px 6px',
                                                                borderRadius: '3px',
                                                                fontSize: '10px',
                                                                fontWeight: '600',
                                                                marginLeft: '8px'
                                                            }}>
                                                                ADMIN
                                                            </span>
                                                        )}
                                                    </div>
                                                    <p className="activity-date landing-activity-date">
                                                        {new Date(activity.start_date).toLocaleDateString()}
                                                    </p>
                                                </div>
                                                <div className="activity-metrics">
                                                    <div>
                                                        <span>Distance</span>
                                                        <strong>{(activity.distance / 1000).toFixed(1)} km</strong>
                                                    </div>
                                                    <div>
                                                        <span>Elevation</span>
                                                        <strong>{activity.total_elevation_gain?.toFixed(0) || 'N/A'} m</strong>
                                                    </div>
                                                    <div>
                                                        <span>Time</span>
                                                        <strong>{formatDuration(activity.moving_time)}</strong>
                                                    </div>
                                                    {/* Water percentage metric (toggled with Ctrl+W) */}
                                                    {showWaterPercentages && activityWaterAnalysis[activity.id] && (
                                                        <div style={{ 
                                                            background: 'rgba(74, 144, 226, 0.1)',
                                                            borderRadius: '4px',
                                                            padding: '4px 6px',
                                                            border: '1px solid rgba(74, 144, 226, 0.3)'
                                                        }}>
                                                            <span style={{ color: '#4A90E2', fontWeight: '500' }}>Water</span>
                                                            <strong style={{ color: '#4A90E2' }}>
                                                                {activityWaterAnalysis[activity.id].waterPercentage}%
                                                            </strong>
                                                        </div>
                                                    )}
                                                    {activity.type?.toLowerCase().includes('run') && activity.average_speed ? (
                                                        <div>
                                                            <span>Pace</span>
                                                            <strong>{(() => {
                                                                const paceSecondsPerKm = (1 / activity.average_speed) * 1000;
                                                                const paceMinutes = Math.floor(paceSecondsPerKm / 60);
                                                                const paceSeconds = Math.floor(paceSecondsPerKm % 60);
                                                                return `${paceMinutes}:${paceSeconds.toString().padStart(2, '0')} /km`;
                                                            })()}</strong>
                                                        </div>
                                                    ) : activity.average_watts ? (
                                                        <div>
                                                            <span>Power</span>
                                                            <strong>{activity.average_watts.toFixed(0)} W</strong>
                                                        </div>
                                                    ) : null}
                                                </div>
                                                {/* View on Strava link */}
                                                <div className="activity-actions">
                                                    <a 
                                                        href={`https://www.strava.com/activities/${activity.id}`}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="strava-activity-link"
                                                        onClick={(e) => {
                                                            e.stopPropagation(); // Prevent activity selection when clicking link
                                                            posthog?.capture('view_on_strava_clicked', {
                                                                source: 'landing_page_activity',
                                                                strava_activity_id: activity.id,
                                                                activity_name: activity.name
                                                            });
                                                        }}
                                                    >
                                                        View on Strava
                                                        <i className="ti ti-external-link" style={{ marginLeft: '4px', fontSize: '14px' }}></i>
                                                    </a>
                                                </div>
                                                {/* Add ActivityPhotos component in compact mode - Admin only */}
                                                {isAdmin && showActivityPhotos && (
                                                    <React.Suspense fallback={<div className="loading-spinner-small"></div>}>
                                                        <ActivityPhotosAdmin 
                                                            activityId={activity.id?.toString() || activity.id} 
                                                            compact={true}
                                                        />
                                                    </React.Suspense>
                                                )}
                                            </div>
                                            {selectedActivity?.id === activity.id && (
                                                <div className="selection-indicator">
                                                    <i className="ti ti-circle-check" style={{ fontSize: '24px', color: 'var(--Primary-600)' }}></i>
                                                </div>
                                            )}
                                        </li>
                                        );
                                    })}
                                </ul>

                                {totalPages > 1 && (
                                    <div className="pagination-controls">
                                         <button onClick={goToPreviousPage} disabled={currentPage === 1}>
                                            &larr; {t('landing.previous','Previous')}
                                        </button>
                                         <span>{t('landing.pageOf','Page {{current}} of {{total}}', { current: currentPage, total: totalPages })}</span>
                                         <button onClick={goToNextPage} disabled={currentPage === totalPages}>
                                            {t('landing.next','Next')} &rarr;
                                        </button>
                                    </div>
                                )}
                            </>
                        ) : (
                             <p style={{ textAlign: 'center' }}>{stravaConnected ? t('landing.noActivities','No recent activities found.') : t('landing.connectStravaToSee','Connect your Strava account to see activities.')}</p>
                        )}
                    </div>
                </div>
            )}

            {/* Map Styles Section */}
            {user && (
                <div id="styles-section" className="history-page collapsible-section" style={{ marginBottom: '40px' }}>
                    <div 
                        className="share-header-row collapsible-header"
                        style={{ marginBottom: '20px', cursor: 'pointer' }}
                        onClick={toggleStylesSection}
                    >
                        <div>
                            <h2 style={{
                                fontSize: '30px',
                                fontWeight: '500',
                                lineHeight: '1.27',
                                color: 'var(--Gray-900, #101828)',
                                margin: '0 0 4px 0',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '12px'
                            }}>
                                2. {t('landing.selectStyleTitle','Select your Mythic Style')}
                                <i className={`ti ${isStylesSectionExpanded ? 'ti-chevron-up' : 'ti-chevron-down'}`} 
                                   style={{ fontSize: '24px', color: 'var(--Gray-500)' }}></i>
                            </h2>
                            <p className="history-subtitle">
                                {selectedTemplate ? 
                                    `${t('landing.selected','Selected')}: ${selectedTemplateLabel || selectedTemplate.name}` : 
                                    t('landing.stylesSubtitle','All of our Mythic Maps have been crafted with love. We always strive to add new and exciting new styles.')
                                }
                            </p>
                        </div>
                        {selectedTemplate && !((selectedTemplate.mapInputType || '2d') === '3d' && is3DSelectionPending) && (
                            <div className="selection-status">
                                <i className="ti ti-circle-check" style={{ fontSize: '24px', color: 'var(--Primary-600)' }}></i>
                            </div>
                        )}
                    </div>
                    <div className="history-divider"></div>

                    {/* Collapsed Style Preview */}
                    {!isStylesSectionExpanded && selectedTemplate && (
                        <div className="collapsed-style-card">
                            <div className="style-thumbnail">
                                <img
                                    src={selectedTemplate.exampleImageUrl || '/images/default-style.svg'}
                                    alt={`${selectedTemplate.name} Style Map`}
                                    onError={(e) => {
                                        e.target.src = '/images/default-style.svg';
                                    }}
                                />
                            </div>
                            <div className="style-details">
                                <div className="style-title">
                                    <h3>{selectedTemplate.name}</h3>
                                </div>
                                {selectedTemplate.description && (
                                    <p className="style-description">{selectedTemplate.description}</p>
                                )}
                            </div>
                        </div>
                    )}

                    {selectedTemplateGroup?.variants?.length > 1 && (
                        <div
                            id="style-variant-selector"
                            style={{
                            margin: '20px 0',
                            padding: '16px',
                            border: '1px solid var(--Gray-200, #E5E7EB)',
                            borderRadius: '12px',
                            background: 'var(--Gray-50, #F9FAFB)'
                        }}>
                            <p style={{
                                margin: '0 0 12px 0',
                                fontWeight: 600,
                                color: 'var(--Gray-700, #374151)'
                            }}>
                                Choose between 2D and 3D versions:
                            </p>
                            <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                                {selectedTemplateGroup.variants.map((variant) => {
                                    const isVariant3D = (variant.mapInputType || '2d') === '3d';
                                    const variantType = isVariant3D ? '3D Terrain' : '2D Classic';
                                    const isVariantPending = isVariant3D && ((pending3DVariantId && pending3DVariantId === variant.id) || (selectedTemplate?.id === variant.id && is3DSelectionPending));
                                    const isVariantSelected = selectedTemplate?.id === variant.id && !(isVariant3D && is3DSelectionPending);
                                    const variantProgress = isVariantPending ? Math.min(Math.max(threeDRenderProgress, 5), 100) : 0;
                                    return (
                                        <button
                                            key={variant.id}
                                            type="button"
                                            onClick={(event) => {
                                                event.stopPropagation();
                                                if (isVariantPending) {
                                                    return;
                                                }
                                                handleTemplateVariantSelect(variant);
                                            }}
                                            className={`variant-select-button ${isVariantSelected ? 'selected' : ''}`}
                                            style={{
                                                flex: '1 1 180px',
                                                minWidth: '160px',
                                                borderRadius: '12px',
                                                border: isVariantSelected ? '2px solid var(--Primary-500, #A855F7)' : '1px solid var(--Gray-200, #E5E7EB)',
                                                background: isVariantSelected ? 'rgba(168, 85, 247, 0.08)' : 'white',
                                                padding: '10px',
                                                cursor: isVariantPending ? 'wait' : 'pointer',
                                                display: 'flex',
                                                flexDirection: 'column',
                                                alignItems: 'center',
                                                gap: '8px',
                                                transition: 'border-color 0.2s ease, background 0.2s ease',
                                                position: 'relative',
                                                overflow: 'hidden',
                                                opacity: isVariantPending ? 0.6 : 1
                                            }}
                                        >
                                            <div style={{
                                                width: '100%',
                                                borderRadius: '8px',
                                                overflow: 'hidden',
                                                border: '1px solid rgba(17, 24, 39, 0.06)',
                                                background: '#fff'
                                            }}>
                                                <img
                                                    src={variant.exampleImageUrl || '/images/default-style.svg'}
                                                    alt={`${variant.name || variantType}`}
                                                    style={{ width: '100%', height: '110px', objectFit: 'cover', display: 'block' }}
                                                    onError={(e) => {
                                                        e.target.src = '/images/default-style.svg';
                                                    }}
                                                />
                                            </div>
                                            <div style={{ textAlign: 'center' }}>
                                                <div style={{
                                                    fontWeight: 600,
                                                    color: 'var(--Gray-800, #1F2937)'
                                                }}>
                                                    {variant.name || variantType}
                                                </div>
                                                <div style={{
                                                    fontSize: '12px',
                                                    color: 'var(--Gray-500, #6B7280)'
                                                }}>
                                                    {variantType}
                                                </div>
                                            </div>
                                            {isVariantPending && (
                                                <div style={{
                                                    position: 'absolute',
                                                    top: 0,
                                                    left: 0,
                                                    right: 0,
                                                    bottom: 0,
                                                    background: 'rgba(255, 255, 255, 0.85)',
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    gap: '6px',
                                                    padding: '8px',
                                                    zIndex: 2
                                                }}>
                                                    <div className="spinner" style={{ width: '20px', height: '20px', border: '3px solid rgba(15,23,42,0.2)', borderTopColor: 'var(--Primary-600)', borderRadius: '50%', animation: 'spin 1s linear infinite' }}></div>
                                                    <span style={{ fontSize: '12px', color: 'var(--Gray-700, #374151)', fontWeight: 600 }}>
                                                        {t('landing.preparingMap','Preparing map…')}
                                                    </span>
                                                </div>
                                            )}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    )}

                    <div className={`collapsible-content ${isStylesSectionExpanded ? 'expanded' : 'collapsed'}`}>
                        {isLoadingTemplates ? (
                             <p style={{ textAlign: 'center' }}>{t('landing.loadingStyles','Loading map styles...')}</p>
                        ) : defaultStyles.length > 0 ? (
                            <>
                                {/* Water Analysis Summary (toggled with Ctrl+W) */}
                                {isWaterAnalyzed && showWaterAnalysis && showWaterPercentages && (
                                    <div style={{
                                        background: 'var(--Primary-50, #FAFAF9)',
                                        border: '1px solid var(--Primary-200, #E7E5E4)',
                                        borderRadius: '8px',
                                        padding: '16px',
                                        marginBottom: '20px'
                                    }}>
                                        <h4 style={{
                                            margin: '0 0 8px 0',
                                            color: 'var(--Primary-700, #44403C)',
                                            fontSize: '14px',
                                            fontWeight: '600',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '8px'
                                        }}>
                                            <i className="ti ti-droplet" style={{ fontSize: '16px' }}></i>
                                             {t('landing.waterContentAnalysis','Water Content Analysis')}
                                        </h4>
                                        <p style={{
                                            margin: '0 0 8px 0',
                                            color: 'var(--Primary-600, #57534E)',
                                            fontSize: '13px'
                                        }}>
                                            {waterSummary.description}
                                        </p>
                                        <div style={{ display: 'flex', gap: '16px', fontSize: '12px' }}>
                                             <span>{t('landing.water')}:{' '}{waterSummary.percentage}%</span>
                                             <span>{t('landing.recommended')}:{' '}{recommendedStyles.length} {t('landing.styles','styles')}</span>
                                             <span>{t('landing.showing')}:{' '}{defaultStyles.length} {t('landing.styles','styles')}</span>
                                        </div>
                                    </div>
                                )}

                                <ul className="history-requests-list">
                                    {/* Use the properly paginated and filtered currentStyles */}
                                    {currentStyles.map((template, index) => {
                                        const templateId = template.id || `template-${index}`;
                                        const isRecommended = recommendedStyles.some(rec => rec.id === templateId);
                                        const isSelectedTemplate = selectedTemplateGroup && (selectedTemplateGroup.id === templateId || selectedTemplateGroup.groupId === template.groupId);
                                        const showSelectedTemplateState = isSelectedTemplate && !(selectedTemplate?.mapInputType === '3d' && is3DSelectionPending);
                                        const showTemplatePendingOverlay = isSelectedTemplate && selectedTemplate?.mapInputType === '3d' && is3DSelectionPending;
                                        return (
                                            <li
                                                key={template.groupId || templateId}
                                                className={`map-style-card ${showSelectedTemplateState ? 'selected-template' : ''} ${isRecommended ? 'recommended-style' : ''}`}
                                                onClick={() => handleTemplateSelect(template)}
                                                style={{
                                                    ...(isRecommended ? {
                                                        border: '2px solid var(--Primary-300, #D6D3D1)',
                                                        boxShadow: '0px 4px 12px rgba(87, 83, 78, 0.15)'
                                                    } : {}),
                                                    position: 'relative',
                                                    opacity: showTemplatePendingOverlay ? 0.6 : 1
                                                }}
                                            >
                                                <div className="history-thumbnail">
                                                    <img
                                                        src={template.exampleImageUrl || '/images/default-style.svg'}
                                                        alt={`${template.name} Style Map`}
                                                        onError={(e) => {
                                                            e.target.src = '/images/default-style.svg';
                                                        }}
                                                    />
                                                </div>
                                                <div className="history-details" style={{ padding: '20px' }}>
                                                    <h3 className="activity-name" style={{
                                                        margin: '0 0 8px 0',
                                                        fontSize: '18px',
                                                        fontWeight: '600'
                                                    }}>{template.name}</h3>
                                                    {template.description && (
                                                        <p style={{
                                                            margin: '0',
                                                            fontSize: '14px',
                                                            color: 'var(--Gray-500, #667085)',
                                                            lineHeight: '1.5'
                                                        }}>{template.description}</p>
                                                    )}
                                                </div>
                                                {showSelectedTemplateState && (
                                                    <div className="selection-indicator">
                                                        <i className="ti ti-circle-check" style={{ fontSize: '24px', color: 'var(--Primary-600)' }}></i>
                                                    </div>
                                                )}
                                                {showTemplatePendingOverlay && (
                                                    <div style={{
                                                        position: 'absolute',
                                                        top: 0,
                                                        left: 0,
                                                        right: 0,
                                                        bottom: 0,
                                                        background: 'rgba(255, 255, 255, 0.88)',
                                                        display: 'flex',
                                                        flexDirection: 'column',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        gap: '12px',
                                                        padding: '20px',
                                                        zIndex: 3,
                                                        pointerEvents: 'none'
                                                    }}>
                                                        <div className="spinner" style={{ width: '26px', height: '26px', border: '4px solid rgba(15,23,42,0.2)', borderTopColor: 'var(--Primary-600)', borderRadius: '50%', animation: 'spin 1s linear infinite' }}></div>
                                                        <span style={{ fontSize: '14px', color: 'var(--Gray-700, #374151)', fontWeight: 600 }}>
                                                            {t('landing.preparingMap','Preparing map…')}
                                                        </span>
                                                    </div>
                                                )}
                                            </li>
                                        );
                                    })}
                                </ul>

                                {/* Only show pagination on desktop */}
                                {totalStylePages > 1 && !isMobile && styleSortBy !== 'recommended' && (
                                    <div className="pagination-controls">
                                         <button onClick={goToPreviousStylePage} disabled={currentStylePage === 1}>
                                            &larr; {t('landing.previous','Previous')}
                                        </button>
                                         <span>{t('landing.pageOf','Page {{current}} of {{total}}', { current: currentStylePage, total: totalStylePages })}</span>
                                         <button onClick={goToNextStylePage} disabled={currentStylePage === totalStylePages}>
                                            {t('landing.next','Next')} &rarr;
                                        </button>
                                    </div>
                                )}
                            </>
                        ) : promptTemplates.length > 0 ? (
                            <div style={{
                                textAlign: 'center',
                                padding: '40px 20px',
                                background: 'var(--Gray-50, #F9FAFB)',
                                borderRadius: '8px',
                                border: '1px solid var(--Gray-200, #EAECF0)'
                            }}>
                                <i className="ti ti-filter-off" style={{ fontSize: '48px', color: 'var(--Gray-400)', marginBottom: '16px' }}></i>
                                 <h3 style={{ margin: '0 0 8px 0', color: 'var(--Gray-700)' }}>{t('landing.noStyles','No suitable styles found')}</h3>
                                <p style={{ margin: '0', color: 'var(--Gray-500)', fontSize: '14px' }}>
                                     {t('landing.noStylesDesc','Based on the water content analysis ({{pct}}% water), no styles match this route type. Try selecting a different activity or check back later for more styles.', { pct: waterSummary.percentage })}
                                </p>
                                <button 
                                    onClick={() => setWaterAnalysis(null)}
                                    style={{
                                        marginTop: '16px',
                                        padding: '8px 16px',
                                        background: 'var(--Primary-600)',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '6px',
                                        fontSize: '14px',
                                        cursor: 'pointer'
                                    }}
                                >
                                     {t('landing.showAllStyles','Show all styles')}
                                </button>
                            </div>
                        ) : (
                             <p style={{ textAlign: 'center' }}>{t('landing.noMapStyles','No map styles available.')}</p>
                        )}
                    </div>
                </div>
            )}

            {/* Step 3: Build the Myth Section */}
            {user && (
                <div id="build-myth-section" className="history-page" style={{ marginBottom: '40px' }}>
                    <h2 style={{
                        fontSize: '30px',
                        fontWeight: '500',
                        lineHeight: '1.27',
                        color: 'var(--Gray-900, #101828)',
                        margin: '0 0 4px 0'
                    }}>3. {t('landing.buildMythTitle','Build the Myth')}</h2>
                    <p className="history-subtitle">{t('landing.buildMythSubtitle','Let\'s generate the most epic map for your activity')}</p>
                    <div className="history-divider"></div>

                    <div className="generation-section">
                        {/* Generation Button */}
                        <div className="generation-controls">
                            <button
                                onClick={handleGenerateMap}
                                disabled={!selectedActivity || !selectedTemplate || isGenerating || monthlyUsage.remaining <= 0 || ((selectedTemplate?.mapInputType || '2d') === '3d' && is3DSelectionPending)}
                                className="submit-generation-button"
                                style={monthlyUsage.remaining <= 0 ? {
                                    backgroundColor: '#9CA3AF',
                                    borderColor: '#9CA3AF',
                                    cursor: 'not-allowed'
                                } : {}}
                            >
                                 {isGenerating ? t('landing.generating','Generating Your Mythic Map...') : 
                                  monthlyUsage.remaining <= 0 ? t('landing.outOfCredits','You have run out of credits') : 
                                  t('landing.makeItMythic','Make it Mythic')}
                            </button>

                            {/* Show different messages based on state */}
                            {monthlyUsage.remaining <= 0 ? (
                                <div style={{
                                    marginTop: '16px',
                                    padding: '12px',
                                    backgroundColor: '#FEF3F2',
                                    border: '1px solid #FECDCA',
                                    borderRadius: '8px',
                                    textAlign: 'center'
                                }}>
                                    <div style={{
                                        color: '#B42318',
                                        fontWeight: '600',
                                        marginBottom: '4px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        gap: '8px'
                                    }}>
                                        <i className="ti ti-exclamation-triangle" style={{ fontSize: '16px' }}></i>
                                         {t('landing.limitReached','Generation limit reached')}
                                    </div>
                                    <p style={{
                                        color: '#B42318',
                                        fontSize: '14px',
                                        margin: '0'
                                    }}>
                                         {t('landing.usedAllCredits','You\'ve used all {{limit}} of your credits.', { limit: monthlyUsage.monthlyLimit })}
                                    </p>
                                </div>
                            ) : ((selectedTemplate?.mapInputType || '2d') === '3d' && is3DSelectionPending) ? (
                                <div style={{
                                    marginTop: '16px',
                                    padding: '12px',
                                    backgroundColor: '#FFF7ED',
                                    border: '1px solid #FECACA',
                                    borderRadius: '8px',
                                    textAlign: 'center'
                                }}>
                                    <p style={{
                                        color: '#BE123C',
                                        fontSize: '14px',
                                        margin: '0',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        gap: '8px'
                                    }}>
                                        <div className="spinner" style={{ width: '18px', height: '18px', border: '3px solid rgba(190,18,60,0.2)', borderTopColor: '#BE123C', borderRadius: '50%', animation: 'spin 1s linear infinite' }}></div>
                                        {t('landing.preparingMap','Preparing map…')}
                                    </p>
                                </div>
                            ) : (!selectedActivity || !selectedTemplate) ? (
                                <p className="generation-hint">
                                     {t('landing.selectToGenerate','Please select {{what}} to generate your map', { what: (!selectedActivity && !selectedTemplate) ? t('landing.activityAndStyle','an activity and a style') : (!selectedActivity ? t('landing.anActivity','an activity') : t('landing.aStyle','a style')) })}
                                </p>
                            ) : (
                                <div style={{
                                    marginTop: '16px',
                                    padding: '12px',
                                    backgroundColor: '#F0FDF4',
                                    border: '1px solid #BBF7D0',
                                    borderRadius: '8px',
                                    textAlign: 'center'
                                }}>
                                    <p style={{
                                        color: '#16A34A',
                                        fontSize: '14px',
                                        margin: '0',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        gap: '8px'
                                    }}>
                                        <i className="ti ti-check-circle" style={{ fontSize: '16px' }}></i>
                                         {t('landing.ready','Ready to generate! You have {{remain}} generations remaining.', { remain: monthlyUsage.remaining })}
                                    </p>
                                </div>
                            )}
                        </div>

                        {/* Status Messages */}
                        {statusError && (
                            <p className="error" style={{ marginTop: '20px', textAlign: 'center' }}>
                                {statusError}
                            </p>
                        )}


                    </div>
                </div>
            )}
        </div>
    );
}

// Add the ActivityIcon helper component at the top of the file or import from a shared location
const ActivityIcon = ({ type, size = 24 }) => {
    let iconClass = 'ti ti-activity'; // default icon

    switch (type?.toLowerCase()) {
        case 'run':
            iconClass = 'ti ti-run';
            break;
        case 'ride':
        case 'virtualride':
        case 'ebikeride':
            iconClass = 'ti ti-bike';
            break;
        case 'walk':
            iconClass = 'ti ti-walk';
            break;
        case 'swim':
            iconClass = 'ti ti-swimming';
            break;
        case 'hike':
            iconClass = 'ti ti-trekking';
            break;
        case 'workout':
            iconClass = 'ti ti-stretching';
            break;
        default:
            iconClass = 'ti ti-activity';
    }

    return <i className={iconClass} style={{ fontSize: `${size}px` }}></i>;
};

export default LandingPage;
